import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { agentToolLabel } from '../lib/agent-tool-labels';
import { unwrap } from '../lib/api-envelope';
import { fmtCurrency } from '../lib/display-labels';
import { formatCalendarDate, parseInstantDate, todayCalendarDate } from '../lib/calendar-date';
import { effectiveTimeZone } from '../lib/tenant-zone';
import { hasHeardBriefToday, markBriefHeard } from '../lib/brief-spoken';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { classifyLoadFailure, isRefusal } from '../lib/load-failure';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import { OFFICE_TICKET_STATUS_LABELS, TICKET_STATUSES, type TicketStatus } from '../lib/ticket-status';
import { useAuth } from '../contexts/AuthContext';
import { recordAudience, recordPath, type RecordKind } from '../lib/record-paths';
import { briefOpeningLine } from '../lib/agent-opening-line';
import { isBriefAudience } from '../lib/morning-brief';
import { isSalesRole } from '../lib/sales-access';
import { LOG_SALES_MEETING, meetingCardFrom, meetingCardsByMessage } from '../lib/scout-meeting-card';
import type { ScoutMeetingData } from '../types/scout';
import ScoutMeetingCard from '../components/sales/ScoutMeetingCard';
import { StageProposalCard } from '../components/sales/StageProposalCard';
import { canChangeOpportunity } from '../lib/opportunity';
import type { StageProposal } from '../types/opportunity';
import { SCOUT_ASK_STATE } from '../components/sales/BriefMeButton';
import type { StoredBrief } from '../types/morning-brief';
import {
  ArrowLeft,
  Send,
  Plus,
  Trash2,
  Loader2,
  Bot,
  User,
  MessageSquare,
  ChevronRight,
  Wrench,
  Sparkles,
  Mic,
  Square,
  Volume2,
  VolumeX,
  Check,
  Settings,
  PhoneOff,
  Bell,
  X,
  ChevronDown,
  Headphones,
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { useHideAgentFab } from '../components/AgentFab';
import { SpeechQueue } from '../lib/speech-queue';
import { nextChunk, flushChunk } from '../lib/tts-chunking';
import { TurnDetector, HANDS_FREE_TURN, BARGE_IN_TURN } from '../lib/voice-activity';
import { monitorLevel, type LevelMonitor } from '../lib/mic-level';
import { judgeClip, MIN_CLIP_MS } from '../lib/voice-clip';
import { Tooltip } from '../components/ui/tooltip';

// ─── Agent Runs Types ───────────────────────────────────────────────────────
//
// Written to what GET /api/v1/agent-runs actually returns: the route is
// `SELECT * FROM agent_runs WHERE outcome = 'pending'` wrapped in the standard
// envelope, so the rows are RAW snake_case columns. There is no `payload`
// column — the JSON a run carries is `action_data`, and it is nullable.
// Verified against production on 2026-09-11 and against the route source.

/**
 * The whole `agent_runs.agent_name` CHECK vocabulary, read from the live
 * constraint on 2026-09-11 (12 values). The previous declaration named three
 * and the panel's label map covered the same three, so `clock_out_watch` and
 * `monthly_cost_analysis` — 4 of Smith's 54 pending runs — would have rendered
 * with NO TITLE at all.
 */
const AGENT_RUN_TYPES = [
  'clock_out_watch',
  'ot_threshold_guard',
  'anomaly_detector',
  'timesheet_reminder',
  'approval_nudge',
  'stat_holiday_guard',
  'pay_day_prep',
  'end_of_day_wrap',
  'dispatch_suggestion',
  'inspection_reminder',
  'monthly_cost_analysis',
  'form_replication',
] as const;

type AgentRunType = (typeof AGENT_RUN_TYPES)[number];
/** `no_action` is an informational run (api#806) — a wrap or cost analysis nobody has marked read. */
type AgentRunOutcome = 'pending' | 'actioned' | 'dismissed' | 'no_action';

/**
 * A ROW LINKS ONLY WHEN IT CARRIES `ticket_id`. From api#791 the ticket list is
 * built from the SQL rows (ticket_id, job_id, number, value, status) and the
 * model contributes only `summary` and a per-ticket `action_needed` keyed by
 * id. Rows written before that deploy — every row up to the 2026-09-22 23:00
 * UTC wrap — are the model's own output with no id, and stay text: resolving
 * their `ticket_number` here would build a link from a number the model
 * re-typed, and numbers collide across tenants.
 */
interface EndOfDayPayload {
  /** Null on a failed run, which persists the row-built half alone. */
  summary: string | null;
  /** Tickets whose STATUS changed in the wrap's window (api#806), not merely edited. */
  tickets: {
    ticket_id?: string;
    job_id?: string | null;
    ticket_number: string;
    well: string | null;
    value: number | null;
    status: string;
  }[];
  /** Summed server-side; null when no ticket carries a value. */
  total_value: number | null;
  /**
   * Approved, not yet invoiced, oldest first (api#806). ABSENT on every wrap
   * written before that deploy — which is "this wrap did not look", not "nothing
   * is waiting", so the section is omitted rather than rendered empty.
   */
  still_waiting?: {
    ticket_id: string;
    job_id: string | null;
    ticket_number: string;
    well: string | null;
    value: number | null;
    status: string;
    /** 'yyyy-MM-dd', the tenant-local day it was approved. Null when unrecorded. */
    waiting_since: string | null;
    days_waiting: number | null;
  }[];
  still_waiting_total_value?: number | null;
  date: string;
}

interface DispatchPayload {
  /** Set by the worker from the job it was asked about, not by the model. */
  job_id: string;
  job_name: string;
  suggested_crew: { name: string; role: string }[];
  suggested_equipment: { name: string; type: string }[];
  reasoning: string;
}

interface InspectionPayload {
  equipment: { equipment_id: string; name: string; status: string; days_since_inspection: number }[];
}

interface AgentRun {
  id: string;
  company_id: string;
  /** Typed as a plain string: the API can send a value a migration added before this file knew about it. */
  agent_name: string;
  outcome: AgentRunOutcome;
  /** Human-readable, written by the agent, present on every production row. */
  trigger_condition: string | null;
  action_taken: string | null;
  /** The run's JSON. NULLABLE — every `clock_out_watch` row in production has none. */
  action_data: EndOfDayPayload | DispatchPayload | InspectionPayload | Record<string, unknown> | null;
  /** What woke the agent. dispatch_suggestion records `{ job_id }` here. */
  trigger_data?: Record<string, unknown> | null;
  /**
   * Set by the list route (api#806): true on a run that asks for no decision —
   * an end-of-day wrap or a monthly cost analysis, unread, last 7 days. It is
   * what chooses the buttons; which agent_names are informational is the API's
   * call, not a list kept here.
   *
   * Only `true` gets Mark read. ABSENT (a list served before #806) falls back to
   * Approve/Dismiss, which is what every run had then, and Dismiss on an
   * informational run is Mark read server-side — so the fallback is safe too.
   */
  informational?: boolean;
  created_at: string;
}

/**
 * Exhaustive over AGENT_RUN_TYPES. `agentRunLabel` marks anything outside it
 * rather than rendering blank, which is what the three-entry map did.
 */
const AGENT_RUN_LABELS: Record<AgentRunType, string> = {
  clock_out_watch: 'Clock-Out Check',
  ot_threshold_guard: 'Overtime Alert',
  anomaly_detector: 'Anomaly',
  timesheet_reminder: 'Timesheet Reminder',
  approval_nudge: 'Approval Nudge',
  stat_holiday_guard: 'Stat Holiday Check',
  pay_day_prep: 'Pay Day Prep',
  end_of_day_wrap: 'End of Day Wrap',
  dispatch_suggestion: 'Dispatch Suggestion',
  inspection_reminder: 'Inspection Reminder',
  monthly_cost_analysis: 'Monthly Cost Review',
  form_replication: 'Form to Replicate',
};

/**
 * A title for a run. An unmapped agent name is MARKED, following
 * getJobTypeLabel — a blank title reads as a run with nothing in it, and a bare
 * echo reads as data.
 */
function agentRunLabel(agentName: string): string {
  return AGENT_RUN_LABELS[agentName as AgentRunType] ?? `Unknown (${agentName})`;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * A wrap row's status, strict. `officeTicketStatusLabel` coerces an unknown
 * value to Draft — a guessed label on a list whose point is what changed.
 * Legacy aliases never reach here: the wrap reads the live `tickets.status`.
 */
const wrapStatusLabel = (raw: string | null | undefined): string =>
  raw && (TICKET_STATUSES as readonly string[]).includes(raw) ? OFFICE_TICKET_STATUS_LABELS[raw as TicketStatus] : '—';

/** @see fmtCurrency — absence renders '—', not '$0.00'. */
const formatCurrency = (val: number | string | null | undefined) => fmtCurrency(val);

/**
 * The one-line preview under a run's title.
 *
 * `trigger_condition` FIRST, because it is the only field every production row
 * has and the agent wrote it to be read: "13 equipment items with no inspection
 * in 7+ days", "Timesheet open > 14 hours", "Monthly cost analysis for 2026-08".
 * The previous version switched on agent_name and dereferenced a `payload` key
 * that does not exist on the wire — `p.equipment.length` on `undefined` throws,
 * which would have taken the whole panel down the moment it had rows in it.
 *
 * Absent renders as an em dash. Never a fabricated summary.
 */
function getPayloadPreview(run: AgentRun): string {
  const trigger = run.trigger_condition?.trim();
  if (trigger) return trigger;

  const data = run.action_data;
  if (data && Array.isArray((data as InspectionPayload).equipment)) {
    const n = (data as InspectionPayload).equipment.length;
    return `${n} item${n !== 1 ? 's' : ''} need inspection`;
  }
  return '—';
}

// ─── Fallback detail for run types with no panel of their own ────────────────

/**
 * Keys in a run's JSON that name a record this app has a page for. Only these
 * become links; any other id is shown as the value it is. Both spellings are
 * listed because the agents disagree (timesheet_reminder writes camelCase).
 */
const RUN_ID_ROUTES: Record<string, { path: (id: string) => string; label: string }> = {
  job_id: { path: (id) => `/jobs/${id}`, label: 'Open job' },
  jobId: { path: (id) => `/jobs/${id}`, label: 'Open job' },
  ticket_id: { path: (id) => `/tickets/${id}`, label: 'Open ticket' },
  ticketId: { path: (id) => `/tickets/${id}`, label: 'Open ticket' },
  equipment_id: { path: (id) => `/equipment/${id}`, label: 'Open equipment' },
  equipmentId: { path: (id) => `/equipment/${id}`, label: 'Open equipment' },
};

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Labels for the action_data keys production has actually carried (read
 * 2026-09-22). A key outside this map is shown verbatim — a guessed label
 * would read as something the agent said.
 */
const RUN_FIELD_LABELS: Record<string, string> = {
  period_month: 'Period',
  flagged_count: 'Flagged',
  name: 'Name',
  fieldCount: 'Fields',
  validationErrors: 'Validation errors',
  workerNames: 'Workers',
  workerIds: 'Worker ids',
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** A scalar as text; absent is an em dash, a real 0 stays 0. */
function runFieldText(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) {
    if (v.every((x) => typeof x === 'string')) return v.length ? v.join(', ') : 'None';
    return `${v.length} item${v.length !== 1 ? 's' : ''}`;
  }
  if (typeof v === 'object') return '—';
  return String(v);
}

interface RunFallbackView {
  fields: { key: string; label: string; value: string }[];
  links: { key: string; to: string; label: string }[];
}

/**
 * What a run with no dedicated panel shows when expanded: each scalar the run
 * carries, and a link for every id that names a page. The agent's own sentence
 * is the header line above, which stops truncating when the run is open. Ids are read from
 * trigger_data too, because that is where dispatch_suggestion records its job.
 */
function runFallbackView(run: AgentRun): RunFallbackView {
  const fields: RunFallbackView['fields'] = [];
  const links: RunFallbackView['links'] = [];
  const seen = new Set<string>();

  const collect = (source: unknown, showFields: boolean) => {
    if (!isPlainObject(source)) return;
    for (const [key, value] of Object.entries(source)) {
      const route = RUN_ID_ROUTES[key];
      if (route && typeof value === 'string' && UUID_SHAPE.test(value)) {
        const to = route.path(value);
        if (!seen.has(to)) { seen.add(to); links.push({ key, to, label: route.label }); }
        continue;
      }
      if (showFields) fields.push({ key, label: RUN_FIELD_LABELS[key] ?? key, value: runFieldText(value) });
    }
  };
  collect(run.action_data, true);
  collect(run.trigger_data, false);

  return { fields, links };
}

// ─── ElevenLabs Voice Options ────────────────────────────────────────────────

const VOICE_OPTIONS = [
  { id: 'kSDv9EbJ41pJUICMEOOu', name: 'Aria', gender: 'Female', desc: 'Warm & conversational' },
  { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Sarah', gender: 'Female', desc: 'Soft & friendly' },
  { id: 'XB0fDUnXU5powFXDhCwa', name: 'Charlotte', gender: 'Female', desc: 'Clear & professional' },
  { id: 'pFZP5JQG7iQjIQuC4Bku', name: 'Lily', gender: 'Female', desc: 'Gentle & calm' },
  { id: 'pNInz6obpgDQGcFmaJgB', name: 'Adam', gender: 'Male', desc: 'Deep & confident' },
  { id: 'nPczCjzI2devNBz1zQrb', name: 'Brian', gender: 'Male', desc: 'Smooth & natural' },
  { id: 'onwK4e9ZLuTAKqWW03F9', name: 'Daniel', gender: 'Male', desc: 'Authoritative & steady' },
  { id: 'ZQe5CZNOzWyzPSCn5a3c', name: 'James', gender: 'Male', desc: 'Warm & articulate' },
];

const VOICE_STORAGE_KEY = 'fieldiq_tts_voice';

function getStoredVoice(): string {
  return localStorage.getItem(VOICE_STORAGE_KEY) || VOICE_OPTIONS[0].id;
}

/**
 * Hands-free: the mic closes itself when the user stops talking, and reopens
 * when the agent stops. OFF unless the user has turned it on, and remembered
 * once they have — a mic that opens itself is not something to surprise
 * somebody with, and a tap-to-talk user must get exactly what they got before.
 */
const HANDS_FREE_STORAGE_KEY = 'fieldiq_agent_hands_free';

/**
 * Barge-in: the mic stays open THROUGH the agent's read-back, so talking over
 * it cuts it off. Off by default and separately remembered, because it is the
 * one part of this that cannot be proven anywhere but on a real handset — it
 * asks the phone to record and play at the same time, and if the hardware
 * echo cancellation does not hold, the agent hears itself and interrupts
 * itself. Tapping the mic mid-read-back already interrupts and always will;
 * this only removes the tap.
 */
const BARGE_IN_STORAGE_KEY = 'fieldiq_agent_barge_in';

function getStoredFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    // Private browsing can throw on read. A refused preference is the default.
    return false;
  }
}

function storeFlag(key: string, on: boolean): void {
  try {
    localStorage.setItem(key, on ? '1' : '0');
  } catch {}
}

/**
 * An audio clip under this is not speech, whatever the recorder thinks.
 *
 * The API refuses under 1KB outright, so anything smaller than this cannot
 * produce a turn and there is nothing to gain by spending a round trip and a
 * Whisper call on it. Whisper on near-silence does not fail — it invents
 * filler, which arrives as a message the user never spoke.
 */
const MIN_AUDIO_BYTES = 1024;

/**
 * What the voice session is doing, in the user's terms.
 *
 * An exhaustive map keyed on every value rather than a chain of ternaries with
 * a default at the end: a label that falls back is how a state nobody named
 * ends up on screen as whatever the last branch happened to say.
 */
type VoiceState = 'idle' | 'listening' | 'thinking' | 'speaking';

const VOICE_STATE_LABEL: Record<VoiceState, string> = {
  idle: 'Ready',
  listening: 'Listening',
  thinking: 'Thinking',
  speaking: 'Speaking',
};

/**
 * Five bars that move with the microphone.
 *
 * The point is not decoration. With the mic closing itself, the only way a
 * user can tell "it is listening and hearing me" from "it is listening and
 * hearing nothing" is to watch something move — and the second of those is
 * exactly the state that ends in a discarded clip.
 */
function LevelMeter({ level }: { level: number }) {
  return (
    <span className="flex items-end gap-[2px] h-3" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className="w-[3px] rounded-full bg-fieldiq-accent"
          style={{ height: `${Math.max(2, Math.min(12, (level * 5 - i + 1) * 12))}px` }}
        />
      ))}
    </span>
  );
}

/**
 * One tool call, as the user sees it.
 *
 * Lives on two surfaces with two different meanings. While a turn is running
 * it is PROGRESS — every call the agent made, whether it wrote, drafted or
 * refused. Once the turn is over it is a RECORD, and only the calls that
 * actually wrote a row are kept (see `confirmedChips`).
 */
interface ToolChip {
  name: string;
  label: string;
  done: boolean;
  path?: string | null;
  /** Set from the tool_result event's own flags — see ToolResultEvent. */
  draft?: boolean;
  failed?: boolean;
}

/**
 * What survives the turn.
 *
 * A chip is a claim that the row is there to open, so the ones kept are the
 * ones that finished, wrote, and resolved to a route. `path` alone would be
 * enough — fieldiq-api #699 withholds `entities` unless the call was neither a
 * draft nor a failure — but the rule is stated here too rather than inherited,
 * because a chip that outlives its turn and deep-links to a row nobody agreed
 * to create is worse than no chip at all, and this is the last place that can
 * refuse it.
 */
const confirmedChips = (tools: ToolChip[]): ToolChip[] =>
  tools.filter((t) => t.done && !t.draft && !t.failed && !!t.path);

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'tool_use';
  content: string | null;
  tool_name?: string;
  tool_input?: any;
  tool_result?: any;
  created_at: string;
  fromVoice?: boolean;
  transcript?: string;
  /**
   * Kept on the message, not in the loading area, so a confirmed write is
   * still one tap from the row it created after the turn ends. Absent on
   * every message loaded from history: the draft/ok flags are stream-time
   * events rather than columns, so a reload cannot reconstruct them and must
   * not guess.
   */
  tools?: ToolChip[];
  /**
   * Scout's meeting-log card for this turn — the API's `meeting` data
   * (api#1089; lib/scout-meeting-card.ts). From the stream's tool_result
   * event live, and from the stored tool row's `tool_result.meeting` on a
   * reload, so unlike `tools` it IS rebuilt from history.
   */
  scoutCard?: ScoutMeetingData;
}

interface Conversation {
  id: string;
  title: string;
  last_message: string | null;
  updated_at: string;
}

// Entity IDs optionally attached to a tool_result SSE event by fieldiq-api
// PR #58. Present only when the tool was invoked with a known entity ID.
interface ToolResultEntities {
  job_id?: string;
  ticket_id?: string;
  equipment_id?: string;
  invoice_id?: string;
  customer_id?: string;
}

/**
 * WHAT THE CALL DID, AS OPPOSED TO WHAT IT SAID.
 *
 * `draft` and `ok` are RECORDED by the API's executor at the moment the
 * decision is taken (fieldiq-api PR #699 — `hitlGate` sets `draft`, `refuse`
 * and the executor's catch clear `ok`). They are never parsed back out of the
 * result prose here, and must not be: reading a refusal out of a sentence is a
 * second implementation of a decision already taken, and it is wrong the first
 * time anybody rewords the sentence.
 *
 * BOTH ARE OPTIONAL, AND THE ABSENT READING IS THE OLD BEHAVIOUR. A build
 * talking to an API from before #699 receives neither key; `draft !== true` and
 * `ok !== false` then hold, so the chip renders exactly as it did. A default of
 * `false` for `ok` would have blanked every chip against that API instead.
 */
interface ToolResultEvent {
  tool: string;
  summary: string;
  /** The HITL gate drafted instead of writing. Nothing was saved. */
  draft?: boolean;
  /** The call did what was asked. False on a refusal, a refused confirm, or a throw. */
  ok?: boolean;
  entities?: ToolResultEntities;
  /** log_sales_meeting only: its draft or question as data (api#1089). Absent on a refusal and the save. */
  meeting?: unknown;
}

// Entity → detail-page path builder. Every entry here was confirmed to have a
// matching :id-param route in App.tsx (job/ticket/equipment/invoice/customer).
// The route param names differ (:jobId, :ticketId, …) but navigation targets a
// concrete path string, so only the path pattern matters. Entity types without
// a confirmed route are omitted (no deep-link rendered for them).
const ENTITY_ROUTES: Partial<Record<keyof ToolResultEntities, (id: string) => string>> = {
  job_id: (id) => `/jobs/${id}`,
  ticket_id: (id) => `/tickets/${id}`,
  equipment_id: (id) => `/equipment/${id}`,
  invoice_id: (id) => `/invoices/${id}`,
  customer_id: (id) => `/customers/${id}`,
};

// Priority order when a result references multiple entities: the most specific
// work item wins (job → ticket → equipment → invoice → customer).
const ENTITY_PRIORITY: Array<keyof ToolResultEntities> = [
  'job_id',
  'ticket_id',
  'equipment_id',
  'invoice_id',
  'customer_id',
];

// First navigable path for these entities, or null if none resolve. A key
// resolves only when it's in ENTITY_ROUTES AND its ID is a non-empty string.
function resolveEntityPath(entities: ToolResultEntities): string | null {
  for (const key of ENTITY_PRIORITY) {
    const id = entities[key];
    const builder = ENTITY_ROUTES[key];
    if (builder && typeof id === 'string' && id.trim() !== '') {
      return builder(id);
    }
  }
  return null;
}

/**
 * THE FALLBACK — the string this screen shipped with, kept verbatim.
 *
 * What a reader sees when there is no brief to open on: a tenant before its
 * first 07:00 tick, a field worker, or a read that failed. It is never spoken.
 * The mic tap that used to read a greeting aloud now opens the mic and nothing
 * else (see `handleMicClick`).
 */
const DEFAULT_GREETING_TEXT = 'Hey! Tap the mic to get started.';

/**
 * What to tell the reader when a read-back fails, or null to say nothing.
 *
 * The /tts route answers a refusal as JSON, but the request asked for a Blob,
 * so the body has to be read back out of it before it can say anything. The
 * provider's own words are matched for the one cause the reader can act on.
 *
 * An autoplay the browser refused is said only for a reply: the brief is read
 * from an effect with no gesture behind it, and a refusal there is expected.
 */
async function speechFailureMessage(err: unknown, isBrief: boolean): Promise<string | null> {
  const e = err as { name?: string; response?: { status?: number; data?: unknown } };
  if (e?.name === 'AbortError' || e?.name === 'CanceledError') return null;
  if (e?.name === 'NotAllowedError') {
    return isBrief ? null : 'Your browser blocked audio \u2014 tap the speaker on the reply to hear it.';
  }
  const status = e?.response?.status;
  if (!status) return 'Couldn\u2019t read the reply aloud \u2014 the voice service could not be reached.';
  let body = '';
  try {
    const data = e.response?.data as { text?: () => Promise<string> } | undefined;
    body = typeof data?.text === 'function' ? await data.text() : JSON.stringify(data ?? '');
  } catch {}
  if (/quota|credits/i.test(body)) {
    return 'Voice replies are unavailable \u2014 the voice service is out of credits. Replies are still shown as text.';
  }
  return `Couldn\u2019t read the reply aloud \u2014 the voice service refused it (${status}).`;
}

/** The message id the opening line is written and spoken under. */
const BRIEF_SPEECH_ID = 'greeting';

/** 44-byte, zero-length PCM WAV — played once inside the first mic tap to unlock the element. */
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=';

const SUGGESTIONS = [
  "What jobs are scheduled for this week?",
  "Show me all overdue invoices",
  "What's the outstanding balance for all customers?",
  "How much revenue did we do this month?",
  "What equipment is currently in use?",
  "Show me today's dispatch schedule",
];

export default function AIAgentScreen() {
  // The FAB links here — redundant on this screen, and it sits on the send button.
  useHideAgentFab();
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Voice state — native MediaRecorder with fresh-stream-after-TTS pattern
  const [listeningMode, setListeningMode] = useState(false);
  const [handsFree, setHandsFree] = useState(() => getStoredFlag(HANDS_FREE_STORAGE_KEY));
  const [bargeIn, setBargeIn] = useState(() => getStoredFlag(BARGE_IN_STORAGE_KEY));
  const handsFreeRef = useRef(handsFree);
  const bargeInRef = useRef(bargeIn);
  useEffect(() => { handsFreeRef.current = handsFree; }, [handsFree]);
  useEffect(() => { bargeInRef.current = bargeIn; }, [bargeIn]);
  /** 0-1, for the level meter. Only meaningful while the mic is open. */
  const [micLevel, setMicLevel] = useState(0);
  /** Why the last clip was thrown away, shown under the mic so it is not silent. */
  const [dropNotice, setDropNotice] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingElapsed, setRecordingElapsed] = useState(0);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const listeningModeRef = useRef(false);
  const startingRecordingRef = useRef(false);
  const isSpeakingRef = useRef(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  /**
   * Has the shared audio element been played inside a gesture yet? iOS Safari
   * grants autoplay per element only to a `play()` made in a user gesture, and
   * the first mic tap is that gesture. It used to be spent reading the greeting
   * aloud; it is now spent on a silent clip, so the mic opens at once and the
   * first reply can still be heard.
   */
  const audioUnlockedRef = useRef(false);
  const micPermissionGranted = useRef(false);

  /*
   * ONE AudioContext for the life of the screen, built and resumed inside the
   * first mic tap — the same gesture that unlocks the audio element.
   *
   * iOS Safari opens a context created outside a gesture in the `suspended`
   * state, and a suspended analyser hands back an all-zero waveform with no
   * error: the detector would never arm, the mic would stay open forever, and
   * it would present as a dead microphone rather than as a broken context.
   * Never closed, for the same reason the audio element is never torn down.
   */
  const audioCtxRef = useRef<AudioContext | null>(null);
  /** Loudness sampling on the mic currently being recorded. */
  const levelMonitorRef = useRef<LevelMonitor | null>(null);
  /** Turn-taking over those readings. Null whenever hands-free is off. */
  const turnDetectorRef = useRef<TurnDetector | null>(null);
  /** The separate listen used for barge-in while the agent speaks. */
  const bargeMonitorRef = useRef<LevelMonitor | null>(null);
  const bargeStreamRef = useRef<MediaStream | null>(null);
  /** Reached through a ref for the same reason `startRecording` is. */
  const startBargeMonitorRef = useRef<(() => void) | null>(null);
  /** Ditto — the loudness callback runs long after the render that built it. */
  const stopAndSendRef = useRef<((opts?: { byUser?: boolean }) => void) | null>(null);
  /**
   * Bumped whenever a recording is abandoned. A clip is judged asynchronously
   * after the recorder stops (see `stopAndSend`), and a session ended while
   * that judgement is in flight must not then send the clip anyway.
   */
  const clipSeqRef = useRef(0);
  const discardAndContinueRef = useRef<((reason: string) => void) | null>(null);

  // ─── The opening line ──────────────────────────────────────────────────────
  //
  // A NEW conversation opens on this morning's brief instead of "Hey there!".
  // Built in code from GET /briefing/today (src/lib/agent-opening-line.ts) — no
  // model call, so it is on screen before a request could have returned.
  //
  // The SAME queryKey as the Home card, deliberately. A user arriving from Home
  // has the brief in cache already, which is what lets the first mic tap speak
  // it with no await inside the gesture: an await before play() is what iOS
  // Safari treats as "not a user gesture", and the audio element never unlocks.
  const { user, isFieldWorker, hasVertical } = useAuth();
  // Where a record link opens for this reader (lib/record-paths.ts). A field
  // hand's tree has no /jobs, /tickets or /equipment; a link with no page there
  // renders as plain text rather than dropping them on their home screen.
  const recordAudienceForLinks = recordAudience(!!isFieldWorker, hasVertical);
  const linkFor = (kind: RecordKind, id: string) => recordPath(kind, id, recordAudienceForLinks);
  const briefAudience = isBriefAudience((user as any)?.role);
  /*
   * A sales rep reaches this screen through Scout (api#1076). The API opens the
   * chat routes to them and nothing else here: /agent-runs is not on the sales
   * allowlist, so the activity panel and its 60s poll are not drawn or sent —
   * a bell that can only ever say "refused" is noise on the rep's main tool.
   */
  const isSales = isSalesRole((user as any)?.role);
  const { data: brief, isLoading: briefLoading } = useQuery({
    queryKey: ['briefing-today'],
    queryFn: async () => {
      // `unwrap`, not `res.data?.data || res.data`. The API answers
      // { success: true, data: null, reason: 'no_brief_yet' } before the first
      // 07:00 tick, and the `||` returns the truthy ENVELOPE there — which
      // would reach briefOpeningLine as an object with no `facts`.
      const res = await api.briefing.today();
      return unwrap<StoredBrief>(res.data);
    },
    enabled: briefAudience,
  });

  /*
   * null whenever the brief is absent, the role is not the audience, or the
   * read failed — every one of which falls back to the greeting that shipped
   * before this. A disabled query never enters `isLoading`, so a field worker
   * is not made to wait for a request that will not be sent.
   */
  const openingLine = briefOpeningLine(brief);

  /*
   * The mic handler reads the line through a ref. Reaching for `openingLine`
   * directly would pin whichever render installed that handler, and the first
   * mic tap can easily precede the brief's arrival.
   */
  const openingLineRef = useRef<string | null>(null);
  useEffect(() => { openingLineRef.current = openingLine; }, [openingLine]);

  // Voice mode flag — true when last input came from push-to-talk
  const isVoiceModeRef = useRef(false);

  // Streaming state
  const [activeTools, setActiveTools] = useState<ToolChip[]>([]);
  /*
   * A mirror of `activeTools` that the SSE reader can read.
   *
   * The reader is one long-lived async loop, so the `activeTools` it closed
   * over is whatever the render that started it happened to hold — always the
   * empty list. The turn's final message needs the chips as they stand at the
   * `done` event, which is exactly what a render-time value cannot give it.
   */
  const activeToolsRef = useRef<ToolChip[]>([]);
  const updateTools = useCallback((next: (prev: ToolChip[]) => ToolChip[]) => {
    activeToolsRef.current = next(activeToolsRef.current);
    setActiveTools(activeToolsRef.current);
  }, []);
  const [streamingText, setStreamingText] = useState('');

  // TTS state
  const [autoSpeak, setAutoSpeak] = useState(true);
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);
  const [ttsLoading, setTtsLoading] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const [selectedVoice, setSelectedVoice] = useState(getStoredVoice);
  const [showVoicePicker, setShowVoicePicker] = useState(false);

  // Hint shown when auto-record fails (iOS needs gesture)
  const [showTapHint, setShowTapHint] = useState(false);

  // ─── Agent Activity state ──────────────────────────────────────────────────
  const [agentRuns, setAgentRuns] = useState<AgentRun[]>([]);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  /**
   * `/agent?panel=activity` opens the agent-activity panel.
   *
   * This panel is the ONLY surface in the app for pending agent runs — there is
   * no /agent-runs route — so anything elsewhere that counts them (the Home
   * morning brief) has nowhere else to send a reader. The parameter is the way
   * in.
   *
   * The param is CONSUMED: it is stripped from the URL as the panel opens, with
   * `replace` so it leaves no extra history entry. Leaving it in place would
   * make the panel reopen itself every time the user closed it, because the
   * condition that opened it would still be true.
   */
  /**
   * `/agent?scout=talk` — the Scout button on Meetings and Clients
   * (components/sales/ScoutButton.tsx). Draws the "Tap to talk" bar over the
   * input; the tap on it runs the ordinary first-mic-tap path, because that tap
   * is the gesture iOS needs to unlock the mic and the spoken reply. Consumed
   * from the URL like `panel`, so a back-and-forth does not redraw it.
   */
  const [micInvite, setMicInvite] = useState(false);
  useEffect(() => {
    if (searchParams.get('scout') !== 'talk') return;
    setMicInvite(true);
    const next = new URLSearchParams(searchParams);
    next.delete('scout');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('panel') !== 'activity') return;
    setShowActivityPanel(true);
    const next = new URLSearchParams(searchParams);
    next.delete('panel');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  /**
   * The last list load's failure, or null once one succeeds. Its own state
   * because the catch below used to be silent, and a silent catch left
   * `agentRuns` at its initial `[]` — so a dead zone or a refused role read
   * "No pending agent activity", a clean bill of health the screen never had.
   */
  const [agentRunsError, setAgentRunsError] = useState<unknown>(null);
  /** A refusal is an answer: once refused, the 60s poll stops asking. */
  const agentRunsRefusedRef = useRef(false);

  const fetchAgentRuns = useCallback(async () => {
    if (isSales || agentRunsRefusedRef.current) return;
    try {
      const resp = await api.agentRuns.list();
      // `unwrap`, not `resp.data.items`. The route answers
      // { success: true, data: { items: [...] } }, so `resp.data` IS the
      // envelope and `.items` on it is undefined — `|| []` then turned every
      // tenant's pending runs into an empty list, permanently. Smith had 54
      // while this panel said "No pending agent activity" and the bell showed
      // no badge.
      const body = unwrap<{ items?: AgentRun[] }>(resp.data);
      const items = body?.items;
      setAgentRuns(Array.isArray(items) ? items : []);
      setAgentRunsError(null);
    } catch (err) {
      // Not a toast — a poll failing every 60s must not disrupt chat. The panel
      // says it instead, where the empty state would have been.
      if (isRefusal(err)) agentRunsRefusedRef.current = true;
      setAgentRunsError(err ?? new Error('agent runs failed to load'));
    }
  }, [isSales]);

  /**
   * Scout's stage-change proposals (api#1174) are pending agent runs too, and
   * GET /agent-runs lists them as raw rows. They are drawn from
   * GET /opportunities/stage-proposals instead — the shape that names the deal,
   * the customer and both stages — with the same card the deal page uses, and
   * left out of the generic list so one run never shows twice.
   */
  const stageProposalsQuery = useQuery({
    queryKey: ['stage-proposals', 'queue'],
    queryFn: () => api.opportunities.stageProposals().then((r) => (Array.isArray(r.data?.data) ? r.data.data : []) as StageProposal[]),
    enabled: !isSales,
    refetchInterval: 60_000,
  });
  const stageProposals = stageProposalsQuery.data ?? [];
  const isStageProposalRun = (r: AgentRun) => r.agent_name === 'scout' && r.trigger_condition === 'stage_change';
  // Only once the proposals list has ARRIVED: if it failed, the raw rows stay
  // in the generic list rather than vanishing from both.
  const genericRuns = stageProposalsQuery.isSuccess ? agentRuns.filter((r) => !isStageProposalRun(r)) : agentRuns;

  // Poll every 60 seconds
  useEffect(() => {
    fetchAgentRuns();
    const interval = setInterval(fetchAgentRuns, 60000);
    return () => clearInterval(interval);
  }, [fetchAgentRuns]);

  const handleAgentAction = async (id: string, action: 'approve' | 'dismiss' | 'read') => {
    const removed = agentRuns.find(r => r.id === id);
    if (!removed) return;

    // Optimistic removal
    setAgentRuns(prev => prev.filter(r => r.id !== id));

    try {
      if (action === 'approve') {
        await api.agentRuns.approve(id);
        toast.success('Approved');
      } else if (action === 'read') {
        await api.agentRuns.markRead(id);
        toast.success('Marked read');
      } else {
        await api.agentRuns.dismiss(id);
        toast.success('Dismissed');
      }
    } catch (err: any) {
      // Restore on error
      setAgentRuns(prev => [...prev, removed]);
      toast.error(err?.response?.data?.error || err?.response?.data?.message || (action === 'read' ? 'Failed to mark read' : `Failed to ${action}`));
    }
  };

  /**
   * The body of an expanded run: its own panel where one exists and the run
   * carries the data for it, otherwise the fallback (summary, fields, links).
   *
   * GUARDED, because action_data is nullable and the dedicated shapes are
   * largely aspirational: measured on production 2026-09-11, all 49 early
   * end_of_day_wrap rows carry action_data NULL and dispatch_suggestion has
   * never produced a row at all. A dedicated panel whose data did not arrive
   * falls through to the fallback rather than rendering nothing — an expanded
   * run with an empty body reads as a run with nothing in it.
   */
  const renderRunDetail = (run: AgentRun) => {
    if (run.agent_name === 'end_of_day_wrap') {
      const p = run.action_data as EndOfDayPayload | null;
      if (p && Array.isArray(p.tickets)) {
        const waiting = Array.isArray(p.still_waiting) ? p.still_waiting : null;
        /** A row links only when it carries its own id. See EndOfDayPayload. */
        const ticketLabel = (t: { ticket_id?: string; ticket_number: string; well: string | null }) => {
          const label = `${t.ticket_number || '—'} — ${t.well || '—'}`;
          const to = typeof t.ticket_id === 'string' && UUID_SHAPE.test(t.ticket_id) ? linkFor('ticket', t.ticket_id) : null;
          return to ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); navigate(to); }}
              className="text-fieldiq-accent hover:underline font-medium text-left min-h-[44px]"
            >
              {label}
            </button>
          ) : (
            <span className="text-white font-medium">{label}</span>
          );
        };
        const heading = (text: string) => (
          <p className="text-xs text-fieldiq-muted font-semibold uppercase tracking-wider">{text}</p>
        );
        const totalRow = (value: number | null | undefined) => (
          <div className="flex justify-between text-xs font-semibold pt-1 border-t border-fieldiq-border">
            <span className="text-fieldiq-text">Total</span>
            <span className="text-fieldiq-accent">{formatCurrency(value)}</span>
          </div>
        );
        return (
          <div className="space-y-3">
            {p.summary && <p className="text-sm text-fieldiq-text">{p.summary}</p>}

            <section aria-label="Changed since last wrap" className="space-y-1">
              {heading('Changed since last wrap')}
              {p.tickets.length === 0 ? (
                <p className="text-xs text-fieldiq-muted">No ticket changed status.</p>
              ) : (
                <>
                  {p.tickets.map((t, i) => (
                    <div key={t.ticket_id ?? i} className="flex items-center justify-between gap-2 text-xs">
                      <div className="min-w-0">
                        {ticketLabel(t)}
                        <span className="ml-2 text-fieldiq-muted">{wrapStatusLabel(t.status)}</span>
                      </div>
                      <span className="text-fieldiq-accent font-semibold shrink-0">{formatCurrency(t.value)}</span>
                    </div>
                  ))}
                  {totalRow(p.total_value)}
                </>
              )}
            </section>

            {waiting && (
              <section aria-label="Still waiting" className="space-y-1">
                {heading('Still waiting')}
                {waiting.length === 0 ? (
                  <p className="text-xs text-fieldiq-muted">No approved ticket is waiting to be invoiced.</p>
                ) : (
                  <>
                    {waiting.map((t, i) => (
                      <div key={t.ticket_id ?? i} className="flex items-center justify-between gap-2 text-xs">
                        <div className="min-w-0">
                          {ticketLabel(t)}
                          {/* The API's own calendar day, never recomputed here. */}
                          <span className="ml-2 text-fieldiq-muted">since {formatCalendarDate(t.waiting_since) ?? '—'}</span>
                        </div>
                        <span className="text-fieldiq-accent font-semibold shrink-0">{formatCurrency(t.value)}</span>
                      </div>
                    ))}
                    {totalRow(p.still_waiting_total_value)}
                  </>
                )}
              </section>
            )}
          </div>
        );
      }
    }

    if (run.agent_name === 'dispatch_suggestion') {
      const p = run.action_data as DispatchPayload | null;
      if (p && Array.isArray(p.suggested_crew) && Array.isArray(p.suggested_equipment)) {
        return (
          <div className="space-y-2">
            {typeof p.job_id === 'string' && UUID_SHAPE.test(p.job_id) && linkFor('job', p.job_id) ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); navigate(linkFor('job', p.job_id as string) as string); }}
                className="text-sm text-fieldiq-accent hover:underline font-medium text-left min-h-[44px]"
              >
                {p.job_name || '—'}
              </button>
            ) : (
              <p className="text-sm text-white font-medium">{p.job_name || '—'}</p>
            )}
            {p.suggested_crew.length > 0 && (
              <div>
                <p className="text-xs text-fieldiq-muted font-semibold uppercase tracking-wider mb-1">Crew</p>
                {p.suggested_crew.map((c, i) => (
                  <p key={i} className="text-xs text-fieldiq-text">{c.name} — <span className="text-fieldiq-muted">{c.role}</span></p>
                ))}
              </div>
            )}
            {p.suggested_equipment.length > 0 && (
              <div>
                <p className="text-xs text-fieldiq-muted font-semibold uppercase tracking-wider mb-1">Equipment</p>
                {p.suggested_equipment.map((e, i) => (
                  <p key={i} className="text-xs text-fieldiq-text">{e.name} — <span className="text-fieldiq-muted">{e.type}</span></p>
                ))}
              </div>
            )}
            <p className="text-sm text-fieldiq-text">{p.reasoning}</p>
          </div>
        );
      }
    }

    if (run.agent_name === 'inspection_reminder') {
      const p = run.action_data as InspectionPayload | null;
      if (p && Array.isArray(p.equipment)) {
        return (
          <div className="space-y-1">
            {p.equipment.map((eq, i) => (
              <div key={i} className="flex items-center justify-between text-xs min-h-[44px]">
                {linkFor('equipment', String(eq.equipment_id)) ? (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); navigate(linkFor('equipment', String(eq.equipment_id)) as string); }}
                    className="text-fieldiq-accent hover:underline font-medium"
                  >
                    {eq.name}
                  </button>
                ) : (
                  <span className="text-white font-medium">{eq.name}</span>
                )}
                <span className="text-fieldiq-muted">{eq.status} — {eq.days_since_inspection}d since last inspection</span>
              </div>
            ))}
          </div>
        );
      }
    }

    const view = runFallbackView(run);
    return (
      <div className="space-y-2" data-testid="agent-run-fallback">
        {view.fields.length === 0 && view.links.length === 0 && (
          <p className="text-xs text-fieldiq-muted">The agent recorded nothing beyond the line above.</p>
        )}
        {view.fields.length > 0 && (
          <dl className="space-y-1">
            {view.fields.map((f) => (
              <div key={f.key} className="flex items-start justify-between gap-3 text-xs">
                <dt className="text-fieldiq-muted">{f.label}</dt>
                <dd className="text-white font-medium text-right break-words">{f.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {view.links.length > 0 && (
          <div className="flex flex-wrap gap-x-4">
            {view.links.map((l) => (
              <button
                key={l.to}
                type="button"
                onClick={(e) => { e.stopPropagation(); navigate(l.to); }}
                className="text-xs text-fieldiq-accent hover:underline font-medium min-h-[44px]"
              >
                {l.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  /*
   * The first assistant message on a fresh chat.
   *
   * Held until the brief query has SETTLED, so the line is written once rather
   * than written twice and seen to change. `isLoading` is `isPending &&
   * isFetching`, which a disabled query never satisfies — the field-worker and
   * not-the-audience paths therefore fall straight through to the greeting
   * below with no wait at all.
   *
   * Writing the line and speaking it are separate decisions: this effect only
   * writes it. Whether it is read aloud is the effect below, which answers to
   * a per-user, per-day record rather than to this screen's mount.
   */
  const greetingShownRef = useRef(false);
  useEffect(() => {
    if (greetingShownRef.current || messages.length > 0 || conversationId) return;
    if (briefLoading) return;
    greetingShownRef.current = true;
    const greetingMsg: Message = {
      id: BRIEF_SPEECH_ID,
      role: 'assistant',
      content: openingLine ?? DEFAULT_GREETING_TEXT,
      created_at: new Date().toISOString(),
    };
    setMessages([greetingMsg]);
  }, [briefLoading, openingLine, messages.length, conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConversations = async () => {
    try {
      setLoadingHistory(true);
      const resp = await api.aiAgent.listConversations();
      setConversations(resp.data.data || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadConversation = async (convId: string) => {
    try {
      setLoading(true);
      const resp = await api.aiAgent.getMessages(convId);
      const rows: Message[] = resp.data.data || [];
      // The tool rows are read for Scout's card before they are filtered out.
      const cards = meetingCardsByMessage(rows);
      const msgs = rows
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => (cards.has(m.id) ? { ...m, scoutCard: cards.get(m.id) } : m));
      setMessages(msgs);
      setConversationId(convId);
      setView('chat');
    } catch (err) {
      console.error('Failed to load conversation:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (convId: string) => {
    try {
      await api.aiAgent.deleteConversation(convId);
      setConversations((prev) => prev.filter((c) => c.id !== convId));
      if (conversationId === convId) {
        startNewChat();
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setView('chat');
    setInput('');
    greetingShownRef.current = false;
  };

  // ─── TTS Playback ──────────────────────────────────────────────────────────

  /*
   * Read-back plays through ONE audio element for the life of the screen, with
   * a SpeechQueue in front of it. A streaming reply is spoken sentence by
   * sentence as it arrives; a tap on a message's speaker button speaks that
   * whole message in a single request. Both go through a queue, so there is
   * exactly one owner of the element's `ended` event and one definition of
   * "this read-back is over".
   *
   * The element is created once and never torn down before unmount. iOS Safari
   * grants autoplay per element on a user gesture — the first mic tap — and a
   * multi-sentence reply reassigns `.src` on that same unlocked element. A new
   * Audio per sentence would be silent from the second sentence onward.
   */
  const speechQueueRef = useRef<SpeechQueue | null>(null);
  const speakingMessageIdRef = useRef<string | null>(null);
  const selectedVoiceRef = useRef(selectedVoice);
  useEffect(() => { selectedVoiceRef.current = selectedVoice; }, [selectedVoice]);

  /*
   * `startRecording` is defined below and changes identity every render, so the
   * drain handler reaches it through a ref. Capturing it directly would pin the
   * first render's copy for the life of the screen.
   */
  const startRecordingRef = useRef<(() => Promise<void>) | null>(null);

  /** Release the barge-in listen and its own mic stream. Safe to call twice. */
  const stopBargeMonitor = useCallback(() => {
    bargeMonitorRef.current?.stop();
    bargeMonitorRef.current = null;
    if (bargeStreamRef.current) {
      bargeStreamRef.current.getTracks().forEach((t) => t.stop());
      bargeStreamRef.current = null;
    }
  }, []);

  /*
   * Fires once per read-back, after the LAST chunk — which is what the
   * listening-mode auto-restart has to hang off. Bound per chunk it would
   * reopen the mic between sentences and record the agent reading the rest of
   * its own answer back to itself.
   */
  const handleSpeechDrained = useCallback(() => {
    setPlayingMessageId(null);
    speakingMessageIdRef.current = null;
    isSpeakingRef.current = false;
    setIsSpeaking(false);
    stopBargeMonitor();

    if (listeningModeRef.current) {
      startRecordingRef.current?.().catch(() => {
        // getUserMedia needs a gesture on some iOS versions — show tap hint
        setShowTapHint(true);
      });
    }
  }, [stopBargeMonitor]);

  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;
    /*
     * The brief counts as heard when its audio actually STARTS, not when the
     * queue is opened: an autoplay the browser refuses never fires `playing`,
     * so a refused read-aloud is not recorded as one, and a reader who cuts it
     * off with the mic has still heard it and will not hear it again today.
     */
    const onPlaying = () => {
      if (speakingMessageIdRef.current !== BRIEF_SPEECH_ID) return;
      const pending = pendingBriefMarkRef.current;
      if (!pending) return;
      pendingBriefMarkRef.current = null;
      markBriefHeard(pending.userId, pending.today);
    };
    audio.onplaying = onPlaying;
    return () => {
      audio.onplaying = null;
      speechQueueRef.current?.stop();
      audio.pause();
      audio.src = '';
      audioRef.current = null;
    };
  }, []);

  /**
   * Build the shared AudioContext, or hand back the one already built.
   *
   * MUST be called from inside a user gesture the first time. See the ref's
   * declaration for what a suspended context does to the turn detector, and
   * why that failure goes unnoticed rather than reporting itself.
   *
   * Only ever called when hands-free is on: with it off nothing in this screen
   * needs loudness readings, and a tap-to-talk user should not have an audio
   * session renegotiated underneath a read-back that works today.
   */
  const ensureAudioContext = (): AudioContext | null => {
    if (audioCtxRef.current) return audioCtxRef.current;
    const Ctor: typeof AudioContext | undefined =
      (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    try {
      const ctx = new Ctor();
      // A context built in a gesture still opens suspended on some versions.
      ctx.resume?.().catch(() => {});
      audioCtxRef.current = ctx;
      return ctx;
    } catch {
      return null;
    }
  };

  /** Stop judging the mic that is open, and blank the meter. */
  const stopLevelMonitor = useCallback(() => {
    levelMonitorRef.current?.stop();
    levelMonitorRef.current = null;
    turnDetectorRef.current = null;
    setMicLevel(0);
  }, []);

  /**
   * Begin a read-back: abandon anything currently speaking and open a fresh
   * queue for `messageId`.
   *
   * Returns the queue so the caller can feed it — one chunk for a tapped
   * message, many for a streaming reply.
   */
  const beginSpeech = (messageId: string): SpeechQueue | null => {
    stopTTS();

    // Stop the mic stream BEFORE playing TTS to avoid an iOS audio session conflict
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    const audio = audioRef.current;
    if (!audio) return null;

    /*
     * A read-back that fails says so, once. It used to go to the console only,
     * so a voice service that refused every request (ElevenLabs out of credits,
     * 2026-10-22) presented as "replies are no longer spoken" with nothing on
     * screen to say why — and every chunk of every reply failed the same way.
     */
    let reported = false;
    const queue = new SpeechQueue({
      synthesize: async (text, signal) => {
        const resp = await api.aiAgent.tts(text, selectedVoiceRef.current, signal);
        return new Blob([resp.data], { type: 'audio/mpeg' });
      },
      audio,
      onSpeakingChange: (speaking) => {
        isSpeakingRef.current = speaking;
        setIsSpeaking(speaking);
        if (speaking) {
          setTtsLoading(null);
          setPlayingMessageId(speakingMessageIdRef.current);
          // STAGE B, and a no-op unless the user turned barge-in on.
          startBargeMonitorRef.current?.();
        }
      },
      onDrained: handleSpeechDrained,
      onError: (err) => {
        console.error('TTS playback failed:', err);
        if (reported) return;
        reported = true;
        speechFailureMessage(err, messageId === BRIEF_SPEECH_ID).then((msg) => {
          if (msg) toast.error(msg, { id: 'agent-tts-failure' });
        });
      },
    });

    speechQueueRef.current = queue;
    speakingMessageIdRef.current = messageId;
    audio.onended = () => queue.handleEnded();
    audio.onerror = () => queue.handleAudioError();

    isSpeakingRef.current = true;
    setIsSpeaking(true);
    setTtsLoading(messageId);
    return queue;
  };

  /**
   * Speak one whole message in a single request — the per-message speaker
   * button. Deliberately NOT chunked: the text is already on screen and
   * complete, so there is no streaming delay to remove.
   */
  /**
   * Spend the mic tap's gesture on the shared audio element, silently.
   *
   * Only the first time, and only while nothing owns the element: a queue that
   * is playing has already unlocked it, and a clip played under a live queue
   * would fire that queue's `ended`. The handlers are cleared first for the
   * same reason — the last queue's `ended` must not run again for this clip.
   */
  const unlockAudioInGesture = () => {
    /*
     * `isActive`, not "a queue exists". A queue is never cleared when it drains,
     * so the old `speechQueueRef.current` test was true for the rest of the
     * visit after ANY read-back — the brief read on arrival, or one that the
     * browser refused to autoplay — and the mic tap then never unlocked the
     * element. On iOS Safari that leaves every later reply silent.
     */
    if (audioUnlockedRef.current || speechQueueRef.current?.isActive) return;
    const audio = audioRef.current;
    if (!audio) return;
    audioUnlockedRef.current = true;
    audio.onended = null;
    audio.onerror = null;
    audio.src = SILENT_WAV;
    const play = audio.play();
    if (play && typeof play.catch === 'function') play.catch(() => {});
  };

  /*
   * ─── The brief, read aloud — once per user per tenant day ─────────────────
   *
   * Decided ONCE per visit, the moment everything it depends on has settled,
   * and never again on that visit: a mic tap turns auto-speak on, and an
   * effect that re-decided on that change would read the brief out in front
   * of the mic — the bug this replaced.
   *
   * Spoken only when: the opening line on screen IS the brief (never the
   * fallback greeting, never a resumed conversation); auto-speak is on; the
   * tenant's day is known; and this user has not heard it today. Otherwise it
   * is text with its own speaker button, like any other message.
   *
   * Spoken from an effect rather than a gesture, so a browser that refuses
   * autoplay (iOS Safari, a cold tab) stays silent — and is not recorded as
   * heard, because the record is written on the element's `playing` event.
   */
  const briefSpeakDecidedRef = useRef(false);
  const pendingBriefMarkRef = useRef<{ userId: string; today: string } | null>(null);
  const userId: string | null = (user as any)?.id ?? null;
  const briefShownMessage = messages.length === 1 && messages[0].id === BRIEF_SPEECH_ID ? messages[0] : null;
  useEffect(() => {
    if (briefSpeakDecidedRef.current) return;
    if (briefLoading || !briefShownMessage) return;
    briefSpeakDecidedRef.current = true;
    if (!openingLine || briefShownMessage.content !== openingLine) return;
    if (!autoSpeak || !userId || conversationId) return;
    const today = todayCalendarDate(timeZone);
    if (!today || hasHeardBriefToday(userId, today)) return;
    const queue = beginSpeech(BRIEF_SPEECH_ID);
    if (!queue) return;
    pendingBriefMarkRef.current = { userId, today };
    queue.enqueue(openingLine);
    queue.finish();
  }, [briefLoading, briefShownMessage, openingLine, autoSpeak, userId, conversationId, timeZone]); // eslint-disable-line react-hooks/exhaustive-deps

  const playTTS = (text: string, messageId: string) => {
    // Tapping the message that is already playing stops it
    if (playingMessageId === messageId) {
      stopTTS();
      return;
    }
    const queue = beginSpeech(messageId);
    if (!queue) return;
    queue.enqueue(text);
    queue.finish();
  };

  const stopTTS = () => {
    speechQueueRef.current?.stop();
    speechQueueRef.current = null;
    stopBargeMonitor();
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setPlayingMessageId(null);
    setTtsLoading(null);
    speakingMessageIdRef.current = null;
    isSpeakingRef.current = false;
    setIsSpeaking(false);
  };

  /**
   * STAGE B — listen through the agent's own read-back, so talking over it
   * cuts it off with no tap. Does nothing unless the user has turned barge-in
   * on, which is off by default.
   *
   * A SEPARATE mic stream from the recorder's, opened after playback has
   * started. The recorder's stream is still stopped before TTS, because that
   * is the iOS workaround the whole read-back rests on and this must not be
   * allowed to undo it.
   *
   * BARGE_IN_TURN's thresholds are much harder to satisfy than the ordinary
   * ones, because the loudest thing in the room is the phone's own speaker.
   * Echo cancellation is asked for and, on a handset speaker, is the part that
   * cannot be proven from here: if the agent trips this on its own voice it
   * will interrupt itself, which is why it ships off.
   *
   * WHAT A BARGE COSTS: the arm window. Detection takes half a second of
   * speech, and the recording that gets sent starts after it, so the leading
   * fraction of the first word is not in the audio. Keeping the earlier audio
   * is not an option — a MediaRecorder's opening chunk carries the header
   * bytes the rest of the file is read through, so the agent's own voice cannot
   * be trimmed off the front of a clip without making the clip undecodable.
   */
  const startBargeMonitor = async () => {
    if (!bargeInRef.current || !handsFreeRef.current) return;
    if (!listeningModeRef.current) return;
    if (bargeMonitorRef.current) return;
    const ctx = audioCtxRef.current;
    if (!ctx) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      // The read-back can finish while the permission round trip is in flight.
      if (!isSpeakingRef.current || !listeningModeRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      bargeStreamRef.current = stream;
      const detector = new TurnDetector(BARGE_IN_TURN);
      bargeMonitorRef.current = monitorLevel(ctx, stream, (rms, now) => {
        detector.push(rms, now);
        if (!detector.armed) return;
        stopBargeMonitor();
        stopTTS();
        startRecordingRef.current?.().catch(() => setShowTapHint(true));
      });
    } catch {
      stopBargeMonitor();
    }
  };
  startBargeMonitorRef.current = () => { void startBargeMonitor(); };

  // ─── Recording — always uses fresh getUserMedia stream ───────────────────

  const cleanupRecording = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    levelMonitorRef.current?.stop();
    levelMonitorRef.current = null;
    turnDetectorRef.current = null;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      /*
       * Drop the stop handler BEFORE stopping. Cleanup means abandon, and the
       * handler's whole job is to send what was recorded — left bound, tearing
       * the screen down or discarding a clip would post it instead.
       */
      mediaRecorderRef.current.onstop = null;
      try { mediaRecorderRef.current.stop(); } catch {}
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => cleanupRecording();
  }, [cleanupRecording]);

  // The barge-in listen owns a second mic stream of its own, which the
  // recorder cleanup above knows nothing about.
  useEffect(() => {
    return () => stopBargeMonitor();
  }, [stopBargeMonitor]);

  // Gets a FRESH mic stream every time — the key iOS fix
  const startRecording = useCallback(async () => {
    /*
     * Reentrancy guard. Two callers can arrive within the same tick — a mic tap
     * and the drain handler that fires when the last chunk of a reply ends —
     * and the second `getUserMedia` would replace `mediaRecorderRef`
     * mid-recording, orphaning the first recorder. Its chunks are then never
     * sent, so the tap looks like it did nothing.
     *
     * Released in a `finally`: a `getUserMedia` rejection is normal (a denied
     * permission prompt), and leaving the flag raised would disable the mic for
     * the rest of the session with no error anywhere.
     */
    if (startingRecordingRef.current) return;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') return;
    startingRecordingRef.current = true;

    try {
      setShowTapHint(false);

      // Kill any old stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }

      // Fresh mic stream (permission is cached per page session after first grant)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micPermissionGranted.current = true;
      streamRef.current = stream;

      // Auto-detect mime type (iOS Safari → audio/mp4)
      let mimeType = '';
      for (const mime of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', '']) {
        if (mime === '' || MediaRecorder.isTypeSupported(mime)) {
          mimeType = mime;
          break;
        }
      }

      const options: MediaRecorderOptions = {};
      if (mimeType) options.mimeType = mimeType;

      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.start(1000); // 1s timeslice for regular chunks

      /*
       * HANDS-FREE: judge the loudness of this same stream and close the mic
       * when the user stops talking.
       *
       * Skipped entirely with hands-free off, so tap-to-talk keeps the code
       * path it has always had: no analyser, no audio context, no timer.
       */
      stopLevelMonitor();
      const ctx = handsFreeRef.current ? audioCtxRef.current : null;
      if (ctx) {
        const detector = new TurnDetector(HANDS_FREE_TURN);
        turnDetectorRef.current = detector;
        levelMonitorRef.current = monitorLevel(ctx, stream, (rms, now) => {
          const verdict = detector.push(rms, now);
          setMicLevel(detector.meter);
          if (verdict === 'send') {
            stopLevelMonitor();
            stopAndSendRef.current?.();
            return;
          }
          if (verdict === 'cap') {
            stopLevelMonitor();
            /*
             * The ceiling was reached. `armed` is the difference between a
             * long answer and a mic left open in a quiet cab, and it is the
             * only thing standing between near-silence and a Whisper
             * transcript of words nobody said.
             */
            if (detector.armed) stopAndSendRef.current?.();
            else discardAndContinueRef.current?.('No speech heard — still listening.');
          }
        });
      }

      startTimeRef.current = Date.now();
      setIsRecording(true);
      setRecordingElapsed(0);

      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setRecordingElapsed(elapsed);
      }, 1000);
    } finally {
      startingRecordingRef.current = false;
    }
  }, [stopLevelMonitor]);

  /**
   * Throw away what was just recorded and keep the session alive.
   *
   * THE RULE THIS EXISTS FOR: hands-free must never come to rest with
   * listening mode on and the mic shut. That state has no way out and no way
   * to see it — the bar still says the agent is listening, and it is not.
   * Every path that refuses a clip therefore comes through here, which drops
   * it, says why, and opens the mic again.
   *
   * It replaces a bare `return` on a too-small clip, which did exactly the
   * thing above: the turn ended, nothing was sent, nothing reopened, and the
   * only signal was a bar that had stopped meaning anything.
   */
  const discardAndContinue = (reason: string) => {
    chunksRef.current = [];
    cleanupRecording();
    setIsRecording(false);
    setRecordingElapsed(0);
    setDropNotice(reason);

    if (listeningModeRef.current) {
      startRecording().catch(() => setShowTapHint(true));
    }
  };
  discardAndContinueRef.current = discardAndContinue;

  /**
   * Drop a clip that holds no speech, and say so.
   *
   * `byUser` is the mic tapped off by hand. That tap means "stop", so the
   * session ends rather than reopening the mic — a stop that relistens is a
   * button that cannot turn the mic off. Every other refusal keeps the session
   * alive (see `discardAndContinue`).
   */
  const refuseClip = (byUser: boolean, why: 'too_short' | 'no_speech') => {
    const what = why === 'too_short' ? 'Too short' : 'No speech heard';
    if (byUser) {
      exitListeningMode();
      toast.info(`${what} \u2014 nothing sent.`);
      return;
    }
    discardAndContinue(`${what} \u2014 still listening.`);
  };

  /**
   * Stop the recorder and send what it heard — IF it heard anyone.
   *
   * Three gates, cheapest first, and every one of them ends in NOTHING SENT,
   * because Whisper turns silence and half-second taps into "Thanks for
   * watching." and the route answers that as a real turn:
   *
   *   1. under MIN_CLIP_MS since the mic opened;
   *   2. hands-free, and the live detector never armed — it is the screen's own
   *      definition of speech and it has already been listening;
   *   3. the recording itself, decoded and run through that same detector
   *      (`lib/voice-clip.ts`) — the only judge tap-to-talk has.
   */
  const stopAndSend = (opts: { byUser?: boolean } = {}) => {
    const byUser = opts.byUser === true;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    // Read before stopLevelMonitor() drops the detector. Null with hands-free off.
    const liveHeard = turnDetectorRef.current ? turnDetectorRef.current.armed : null;
    const openedMs = Date.now() - startTimeRef.current;
    stopLevelMonitor();
    setIsRecording(false);
    setRecordingElapsed(0);

    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;

    recorder.onstop = async () => {
      const mimeType = recorder.mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const ext = mimeType.includes('mp4') ? 'mp4' : mimeType.includes('webm') ? 'webm' : 'wav';

      if (openedMs < MIN_CLIP_MS || blob.size < MIN_AUDIO_BYTES) {
        refuseClip(byUser, 'too_short');
        return;
      }
      if (liveHeard === false) {
        refuseClip(byUser, 'no_speech');
        return;
      }

      const seq = clipSeqRef.current;
      const verdict = await judgeClip(blob);
      // The session was ended (or another clip begun) while this was judged.
      if (seq !== clipSeqRef.current) return;
      if (verdict.kind === 'too_short') {
        refuseClip(byUser, 'too_short');
        return;
      }
      if (verdict.kind === 'no_speech') {
        refuseClip(byUser, 'no_speech');
        return;
      }

      handleVoiceChat(blob, ext);
    };

    recorder.stop();

    // Stop the stream immediately — don't keep it during API call + TTS
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  // The drain handler reaches startRecording through this ref — see its comment.
  startRecordingRef.current = startRecording;
  // The turn detector reaches stopAndSend through this one, for the same reason.
  stopAndSendRef.current = stopAndSend;

  const exitListeningMode = () => {
    clipSeqRef.current++;
    listeningModeRef.current = false;
    setListeningMode(false);
    setIsRecording(false);
    setRecordingElapsed(0);
    setShowTapHint(false);
    setDropNotice(null);
    setMicLevel(0);
    cleanupRecording();
  };

  /**
   * End the whole voice session, from wherever the user is standing.
   *
   * One function behind all three End buttons, because the three states it has
   * to work from need different things stopped: mid-read-back needs the speech
   * queue abandoned, mid-recording needs the recorder and its stream released,
   * and a barge-in listen needs its own second stream closed. An End that only
   * covered the state it was drawn in is how a button ends up ending the
   * session on screen while the mic is still open.
   */
  const endSession = () => {
    stopTTS();
    stopLevelMonitor();
    stopBargeMonitor();
    exitListeningMode();
  };

  /*
   * Both switches write the ref as well as the state, because the loudness
   * callback and the recorder callbacks read the ref — a value that only
   * lands on the next render is a value they would miss for the turn in
   * progress, which is precisely the turn the user just changed their mind
   * about.
   *
   * A toggle is a user gesture, and that is the whole reason the context is
   * built here: it is the earliest moment iOS will hand back a running one,
   * and the first mic tap afterwards then has a working detector rather than
   * a silently suspended one.
   */
  const toggleHandsFree = () => {
    const next = !handsFree;
    setHandsFree(next);
    handsFreeRef.current = next;
    storeFlag(HANDS_FREE_STORAGE_KEY, next);
    if (next) {
      ensureAudioContext();
    } else {
      stopLevelMonitor();
      stopBargeMonitor();
    }
  };

  const toggleBargeIn = () => {
    const next = !bargeIn;
    setBargeIn(next);
    bargeInRef.current = next;
    storeFlag(BARGE_IN_STORAGE_KEY, next);
    if (!next) stopBargeMonitor();
  };

  const handleMicClick = async () => {
    /*
     * A tap is a user gesture, which is the only moment iOS will hand back a
     * running AudioContext. Built here rather than only on the first tap so
     * that turning hands-free on mid-session works from the very next tap.
     * Idempotent, and skipped outright while hands-free is off.
     */
    if (handsFreeRef.current) ensureAudioContext();

    if (isRecording) {
      // Currently recording → stop, and send only if someone spoke
      stopAndSend({ byUser: true });
      return;
    }

    /*
     * Barge-in. A tap while the agent is speaking stops playback immediately,
     * drops every chunk still queued, and aborts the requests still in flight —
     * then opens the mic.
     *
     * This has to come before the listening-mode branch below, which reads
     * `!isSpeakingRef.current` and would otherwise treat the tap as "leave
     * listening mode", and before the `loading` guard, because with chunked
     * read-back speech starts while the reply is still streaming and `loading`
     * is still true.
     *
     * Previously a tap here fell through to the plain start-recording path and
     * left the audio playing: the mic recorded the agent talking over itself.
     */
    if (isSpeakingRef.current) {
      stopTTS();
      listeningModeRef.current = true;
      setListeningMode(true);
      try {
        await startRecording();
      } catch {
        exitListeningMode();
      }
      return;
    }

    if (listeningMode && !isRecording && !isSpeakingRef.current) {
      // In listening mode but not recording (e.g. tap hint showing) → start recording
      if (showTapHint) {
        try {
          await startRecording();
        } catch {
          // Still can't get mic — exit listening mode
          exitListeningMode();
        }
        return;
      }
      // Tap to exit listening mode
      exitListeningMode();
      return;
    }

    if (loading || voiceLoading) return;
    if (!autoSpeak) setAutoSpeak(true);

    // Opening the mic never speaks anything first — not the brief, not a
    // greeting, not an earlier reply. The brief is read aloud on its own
    // schedule (see the effect after `beginSpeech`); a tap means "listen".
    unlockAudioInGesture();
    listeningModeRef.current = true;
    setListeningMode(true);

    try {
      await startRecording();
    } catch (err) {
      console.error('startRecording failed:', err);
      exitListeningMode();
    }
  };

  // ─── Shared SSE stream consumer ─────────────────────────────────────────────

  const consumeSSEStream = async (
    resp: Response,
    opts: {
      fromVoice?: boolean;
      placeholderId?: string;
      onConversationEnded?: () => void;
    } = {},
  ) => {
    if (!resp.ok) {
      const errBody = await resp.text();
      let errMessage = 'Something went wrong. Please try again.';
      try { errMessage = JSON.parse(errBody).error || errMessage; } catch {}
      /*
       * The STATUS rides with the message. The voice caller has to tell a
       * rejected clip apart from a real failure, and it must not do that by
       * matching the prose: the route already took that decision, and reading
       * it back out of a sentence is a second implementation of it that goes
       * wrong the first time anybody rewords the sentence.
       */
      const err = new Error(errMessage) as Error & { status?: number };
      err.status = resp.status;
      throw err;
    }

    const reader = resp.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';
    let accumulatedText = '';
    /*
     * The card for THIS turn: the last log_sales_meeting call's `meeting`.
     * A later call with none (a refusal, the save) clears it, so a superseded
     * draft is never attached to the reply.
     */
    let turnCard: ScoutMeetingData | null = null;
    const assistantMsgId = `resp-${Date.now()}`;

    /*
     * Streaming read-back state. `autoSpeak` is read ONCE here rather than at
     * each delta: toggling the speaker mid-reply should not leave half a reply
     * spoken and half not, and a queue that starts without an owner never
     * drains.
     *
     * `spokenTo` is the index in `accumulatedText` already handed to the queue.
     * Everything after it is either unsafe to cut yet or still arriving.
     */
    const speakChunked = autoSpeak;
    let speechQueue: SpeechQueue | null = null;
    let spokenTo = 0;
    let chunksQueued = 0;

    /** Cut and enqueue every chunk the buffer now allows. */
    const drainChunks = () => {
      if (!speakChunked) return;
      for (;;) {
        const r = nextChunk(accumulatedText, spokenTo, chunksQueued === 0);
        if (!r.chunk) return;
        if (!speechQueue) {
          speechQueue = beginSpeech(assistantMsgId);
          if (!speechQueue) return;
        }
        speechQueue.enqueue(r.chunk);
        spokenTo = r.consumed;
        chunksQueued++;
      }
    };

    /**
     * Speak whatever is left and close the queue.
     *
     * `finish()` is what releases the listening-mode auto-restart, so it has to
     * run on every path that ends a reply — including a stream that stops
     * without a `done` event. Idempotent: a second call after the first is a
     * no-op inside the queue.
     */
    const finishSpeech = () => {
      if (!speakChunked) return;
      const tail = flushChunk(accumulatedText, spokenTo);
      if (tail.chunk) {
        if (!speechQueue) speechQueue = beginSpeech(assistantMsgId);
        speechQueue?.enqueue(tail.chunk);
        spokenTo = tail.consumed;
        chunksQueued++;
      }
      speechQueue?.finish();
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Parse SSE events from buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // keep incomplete line in buffer

      let currentEvent = '';
      for (const line of lines) {
        if (line.startsWith('event:')) {
          currentEvent = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          const dataStr = line.slice(5).trim();
          if (!dataStr || !currentEvent) continue;

          try {
            const data = JSON.parse(dataStr);

            switch (currentEvent) {
              case 'voice_meta':
                // Voice-only (first event): show what Whisper heard
                if (opts.placeholderId) {
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === opts.placeholderId
                        ? { ...m, content: data.transcript, transcript: data.transcript }
                        : m
                    )
                  );
                }
                break;

              case 'voice_done':
                // Voice-only (final event): drive mic auto-close.
                // `onConversationEnded` clears listening mode, so it must run
                // before the queue drains — the drain handler reads that flag
                // to decide whether to reopen the mic.
                if (data.conversationEnded) {
                  opts.onConversationEnded?.();
                }
                finishSpeech();
                break;

              case 'tool_start':
                updateTools((prev) => [
                  ...prev,
                  { name: data.tool, label: agentToolLabel(data.tool), done: false },
                ]);
                break;

              case 'tool_result': {
                const tr = data as ToolResultEvent;

                /*
                 * A CHIP IS A CLAIM THAT THE ROW IS THERE TO OPEN.
                 *
                 * `extractEntities` on the API side reads the tool INPUT, so
                 * before #699 it handed this screen a `job_id` the tool had just
                 * refused as missing or foreign, and a `ticket_id` on a DRAFT
                 * that had written nothing — a chip deep-linking to a 404, or to
                 * a record the user has not agreed to create.
                 *
                 * #699 already withholds `entities` unless `ok && !draft`, so
                 * this condition is the same rule stated on the reading side
                 * rather than a second one: a payload that carries entities AND
                 * says it drafted or failed is a contradiction, and the flags —
                 * which are recorded, not inferred — are what we believe.
                 */
                const draft = tr.draft === true;
                const failed = tr.ok === false;
                if (tr.tool === LOG_SALES_MEETING) turnCard = meetingCardFrom(tr.meeting);
                const path = tr.entities && !draft && !failed ? resolveEntityPath(tr.entities) : null;
                updateTools((prev) =>
                  prev.map((t) => (t.name === tr.tool ? { ...t, done: true, path, draft, failed } : t))
                );
                break;
              }

              case 'text_delta':
                accumulatedText += data.delta;
                setStreamingText(accumulatedText);
                // Speak whatever is now safe to cut — this is the whole point:
                // the first sentence is audible long before the last arrives.
                drainChunks();
                break;

              case 'done':
                if (!conversationId && data.conversationId) {
                  setConversationId(data.conversationId);
                }
                /*
                 * Finalize the assistant message — AND hand it the turn's
                 * confirmed writes.
                 *
                 * The chips used to be drawn inside the loading area and
                 * cleared right here, so a chip for a row the agent had just
                 * created vanished at the instant the user was told about it.
                 * Carried on the message they stay where the answer is, and
                 * stay one tap from the row.
                 *
                 * Read off the ref: this loop closed over the render that
                 * started it, and that render's `activeTools` was empty.
                 */
                const turnChips = confirmedChips(activeToolsRef.current);
                const finalMsg: Message = {
                  id: assistantMsgId,
                  role: 'assistant',
                  content: accumulatedText,
                  fromVoice: opts.fromVoice,
                  created_at: new Date().toISOString(),
                  ...(turnChips.length ? { tools: turnChips } : {}),
                  // Scout's card, straight off this turn's tool_result event.
                  ...(turnCard ? { scoutCard: turnCard } : {}),
                };
                setMessages((prev) => [...prev, finalMsg]);
                setStreamingText('');
                updateTools(() => []);
                setLoading(false);

                /*
                 * Read-back is already under way — everything up to the last
                 * safe cut has been spoken. This flushes the held-back tail
                 * and closes the queue.
                 *
                 * It applies to typed replies as well as voice ones: the old
                 * comment here said "if voice mode is on", but the gate was
                 * `autoSpeak` alone and always has been, so a typed reply spoke
                 * too — after the same silence this change removes.
                 */
                finishSpeech();
                break;

              case 'error':
                const errMsg: Message = {
                  id: `err-${Date.now()}`,
                  role: 'assistant',
                  content: data.message || 'Something went wrong.',
                  created_at: new Date().toISOString(),
                };
                setMessages((prev) => [...prev, errMsg]);
                setStreamingText('');
                updateTools(() => []);
                setLoading(false);
                break;
            }
          } catch {
            // Ignore malformed JSON
          }
          currentEvent = '';
        }
      }
    }

    // A stream that ends without a `done` event still has to close the queue,
    // or listening mode never reopens the mic.
    finishSpeech();

    // If stream ended without a done event, finalize
    if (accumulatedText && streamingText) {
      const finalMsg: Message = {
        id: assistantMsgId,
        role: 'assistant',
        content: accumulatedText,
        fromVoice: opts.fromVoice,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, finalMsg]);
    }
  };

  // ─── Voice Chat Pipeline ───────────────────────────────────────────────────

  const handleVoiceChat = async (audioBlob: Blob, ext = 'wav') => {
    // A new turn abandons the previous reply's read-back — no audio from the
    // answer to the last question may play over the answer to this one.
    stopTTS();
    isVoiceModeRef.current = true;
    setMicInvite(false);
    /*
     * Cleared HERE and not when the mic reopens. The notice exists to explain a
     * clip that was thrown away, and the very next thing that happens after
     * one is thrown away is the mic reopening — cleared there, it would be
     * written and wiped in the same tick and no user would ever read it. It
     * goes when a turn actually gets sent, or when the session ends.
     */
    setDropNotice(null);
    setVoiceLoading(true);
    setLoading(true);
    updateTools(() => []);
    setStreamingText('');

    // Add a placeholder user message
    const placeholderMsg: Message = {
      id: `voice-${Date.now()}`,
      role: 'user',
      content: 'Transcribing...',
      fromVoice: true,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, placeholderMsg]);

    try {
      const resp = await api.aiAgent.voiceChat(audioBlob, conversationId || undefined, ext);

      /*
       * A clip the route heard as silence, filler or a Whisper hallucination
       * comes back as a JSON 200 `{ success, transcript: "", ignored: true }`
       * (api#1291), NOT as an event stream — nothing was sent to the agent.
       * That is the same verdict the old 400 carried, so it gets the same
       * answer: drop the clip, say so, keep listening.
       *
       * Any other JSON on a 200 is not a stream and not a verdict, so it is a
       * failure and takes the error path below. A non-2xx is left to
       * consumeSSEStream, which throws with the status (the 400 branch).
       */
      const contentType = resp.headers?.get?.('content-type') ?? '';
      if (resp.ok && contentType.includes('application/json')) {
        let body: { ignored?: unknown; error?: unknown } | null = null;
        try { body = await resp.json(); } catch { body = null; }
        if (body?.ignored === true) {
          setMessages((prev) => prev.filter((m) => m.id !== placeholderMsg.id));
          setVoiceLoading(false);
          setLoading(false);
          // A session ended while the clip was in flight stays ended; only a
          // live one reopens the mic and says it is still listening.
          if (listeningModeRef.current) {
            discardAndContinue('Didn’t catch that — still listening.');
          }
          return;
        }
        throw new Error(
          typeof body?.error === 'string' && body.error
            ? body.error
            : 'Voice processing failed. Please try again.',
        );
      }

      await consumeSSEStream(resp, {
        fromVoice: true,
        placeholderId: placeholderMsg.id,
        onConversationEnded: () => {
          // Exit listening mode BEFORE TTS plays so mic stays closed when audio ends
          listeningModeRef.current = false;
          setListeningMode(false);
        },
      });
    } catch (err: any) {
      // Remove placeholder — nothing was said, so nothing should be on screen.
      setMessages((prev) => prev.filter((m) => m.id !== placeholderMsg.id));

      /*
       * A 400 from the voice route is a verdict on the AUDIO: too small for
       * the route, or nothing Whisper could make words out of. In a yard with
       * a truck running that is an ordinary event, and ending the session over
       * it is the wrong answer — drop the clip, say so, open the mic again.
       *
       * Every other status is a real failure and still ends the session, which
       * is what this whole branch did before.
       */
      if (err?.status === 400 && listeningModeRef.current) {
        setVoiceLoading(false);
        setLoading(false);
        discardAndContinue('Didn\u2019t catch that \u2014 still listening.');
        return;
      }

      const errorMsg: Message = {
        id: `err-${Date.now()}`,
        role: 'assistant',
        content: err.message || 'Voice processing failed. Please try again.',
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
      // Exit listening mode on error so UI doesn't get stuck
      endSession();
    } finally {
      setVoiceLoading(false);
      setLoading(false);
    }
  };

  // ─── Send Text Message (SSE Streaming) ─────────────────────────────────────

  const sendMessage = async (text?: string) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    // A new turn abandons the previous reply's read-back — see handleVoiceChat.
    stopTTS();
    isVoiceModeRef.current = false;
    // The rep has started; the "Tap to talk" invite has done its job.
    setMicInvite(false);
    setInput('');
    updateTools(() => []);
    setStreamingText('');

    const userMsg: Message = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: msg,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const resp = await api.aiAgent.chatStream({
        conversationId: conversationId || undefined,
        message: msg,
        voice_mode: isVoiceModeRef.current,
      });

      await consumeSSEStream(resp);
    } catch (err: any) {
      const errorMsg: Message = {
        id: `err-${Date.now()}`,
        role: 'assistant',
        content: err.message || 'Something went wrong. Please try again.',
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setStreamingText('');
      updateTools(() => []);
      setLoading(false);
    }
  };

  /*
   * "Brief me" on a client (components/sales/BriefMeButton.tsx) arrives with
   * the question in router state. Asked ONCE, then the state is replaced, so
   * Back and a reload do not ask it again. Through a ref because sendMessage
   * changes identity every render.
   */
  const location = useLocation();
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;
  const askedFromStateRef = useRef(false);
  useEffect(() => {
    const ask = (location.state as Record<string, unknown> | null)?.[SCOUT_ASK_STATE];
    if (askedFromStateRef.current || typeof ask !== 'string' || !ask.trim()) return;
    askedFromStateRef.current = true;
    navigate(location.pathname + location.search, { replace: true, state: null });
    sendMessageRef.current(ask);
  }, [location, navigate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Message and conversation stamps are instants, shown on the TENANT's clock
  // (companies.time_zone), not the device's. The reader's locale still picks
  // the format, as before. Absent, unparseable or no zone: an em dash.
  const formatTime = (ts: string) => {
    const d = parseInstantDate(ts);
    if (!d || !timeZone) return '—';
    try {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: effectiveTimeZone(timeZone, d) });
    } catch {
      return '—';
    }
  };

  const formatDate = (ts: string) => {
    const d = parseInstantDate(ts);
    if (!d) return '—';
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (!timeZone) return '—';
    try {
      return d.toLocaleDateString(undefined, { timeZone: effectiveTimeZone(timeZone, d) });
    } catch {
      return '—';
    }
  };

  /*
   * Read off what is actually happening, in priority order. Speaking wins over
   * thinking because chunked read-back starts while the reply is still
   * streaming, so `loading` is still true when the first sentence is audible.
   */
  const voiceState: VoiceState =
    isSpeaking ? 'speaking'
      : (voiceLoading || loading) ? 'thinking'
        : isRecording ? 'listening'
          : 'idle';

  const formatRecordingTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // Simple markdown-ish rendering for assistant messages
  const renderContent = (text: string) => {
    return text.split('\n').map((line, i) => {
      let processed = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      processed = processed.replace(/`(.*?)`/g, '<code class="bg-fieldiq-dark px-1 rounded text-fieldiq-accent text-xs">$1</code>');
      processed = processed.replace(/\$[\d,]+\.?\d*/g, '<span class="text-fieldiq-accent font-semibold">$&</span>');

      if (line.startsWith('- ') || line.startsWith('• ')) {
        return (
          <div key={i} className="flex gap-2 ml-2">
            <span className="text-fieldiq-muted">•</span>
            <span dangerouslySetInnerHTML={{ __html: processed.replace(/^[•-]\s*/, '') }} />
          </div>
        );
      }
      if (line.match(/^\d+\.\s/)) {
        return (
          <div key={i} className="ml-2" dangerouslySetInnerHTML={{ __html: processed }} />
        );
      }
      if (line === '') return <div key={i} className="h-2" />;
      return <div key={i} dangerouslySetInnerHTML={{ __html: processed }} />;
    });
  };

  /**
   * The tool chips, drawn the same way whether the turn is still running or
   * over. One renderer rather than two, because the two lists are the same
   * rows and a second copy is how the running one and the kept one drift into
   * looking like different things.
   */
  const renderToolChips = (tools: ToolChip[]) => (
    <div className="space-y-1.5 mb-2">
      {tools.map((tool, i) => {
        /*
         * A finished call that did NOT do what was asked must not wear the
         * same green check as one that did. The flag is the API's own `ok`, so
         * this is a rendering of a recorded outcome rather than a reading of
         * the summary prose.
         */
        const icon = !tool.done ? (
          <Loader2 size={12} className="animate-spin text-fieldiq-accent shrink-0" />
        ) : tool.failed ? (
          <X size={12} className="text-red-400 shrink-0" />
        ) : (
          <Check size={12} className="text-green-400 shrink-0" />
        );
        const body = (
          <>
            {icon}
            <span className={tool.done ? 'text-fieldiq-muted' : 'text-fieldiq-text'}>
              {tool.label}{tool.done ? '' : '...'}
            </span>
            {/* The gate drafted. Nothing was written, and the row says so. */}
            {tool.draft && (
              <span className="text-[10px] text-amber-400 bg-amber-500/15 border border-amber-500/30 rounded-full px-2 py-0.5 shrink-0">
                Draft — not saved yet
              </span>
            )}
          </>
        );
        return tool.path ? (
          <button
            key={i}
            type="button"
            onClick={() => { if (tool.path) navigate(tool.path); }}
            className="w-full flex items-center gap-2 text-xs text-left min-h-[44px] -mx-2 px-2 rounded-lg cursor-pointer hover:bg-muted transition-colors"
          >
            {body}
            <ChevronRight className="w-4 h-4 text-fieldiq-muted shrink-0 ml-auto" />
          </button>
        ) : (
          <div key={i} className="flex items-center gap-2 text-xs">
            {body}
          </div>
        );
      })}
    </div>
  );

  // ─── History View ────────────────────────────────────────────────────────

  if (view === 'history') {
    return (
      <div className="min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
        {/* Header */}
        <div className="border-b border-border px-6 py-4">
          <button onClick={() => setView('chat')} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
            <ArrowLeft size={24} /> Back
          </button>
          <div className="flex items-center justify-between mt-2">
            <h1 className="text-base font-bold text-foreground">Chat History</h1>
            <div className="flex items-center gap-3">
              <button
                onClick={startNewChat}
                className="bg-fieldiq-accent text-black px-3 py-1.5 rounded-lg text-sm font-semibold"
              >
                <Plus size={16} className="inline mr-1" />
                New
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-2">
          {loadingHistory ? (
            <div className="flex justify-center py-12">
              <Loader2 size={24} className="animate-spin text-fieldiq-accent" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-12 text-fieldiq-muted">
              <MessageSquare size={40} className="mx-auto mb-3 opacity-50" />
              <p>No conversations yet</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.id}
                className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 flex items-center gap-3"
              >
                <button
                  onClick={() => loadConversation(conv.id)}
                  className="flex-1 text-left"
                >
                  <p className="text-white font-medium text-sm truncate">{conv.title}</p>
                  {conv.last_message && (
                    <p className="text-fieldiq-muted text-xs mt-1 truncate">{conv.last_message}</p>
                  )}
                  <p className="text-fieldiq-muted text-xs mt-1">{formatDate(conv.updated_at)}</p>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteConversation(conv.id); }}
                  className="touch-target-sm text-fieldiq-muted hover:text-fieldiq-danger"
                >
                  <Trash2 size={16} />
                </button>
                <ChevronRight size={16} className="text-fieldiq-muted" />
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ─── Chat View ───────────────────────────────────────────────────────────

  return (
    <div className="h-full bg-fieldiq-dark flex flex-col">
      {/* Header */}
      <div className="border-b border-border px-6 py-4 shrink-0">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-base font-bold text-foreground">FieldIQ Agent</h1>
          <div className="flex items-center gap-3">
            <Tooltip content={autoSpeak ? 'Voice mode ON' : 'Voice mode OFF'}>
              <button
                onClick={() => { setAutoSpeak(!autoSpeak); if (autoSpeak) stopTTS(); }}
                className={`touch-target-sm ${autoSpeak ? 'text-fieldiq-accent' : 'text-fieldiq-muted'}`}
                aria-label={autoSpeak ? 'Voice mode ON' : 'Voice mode OFF'}
              >
                {autoSpeak ? <Volume2 size={20} /> : <VolumeX size={20} />}
              </button>
            </Tooltip>
            <Tooltip content={handsFree ? 'Hands-free ON — the mic closes and reopens on its own' : 'Hands-free OFF — tap to talk'}>
              <button
                onClick={toggleHandsFree}
                className={`touch-target-sm ${handsFree ? 'text-fieldiq-accent' : 'text-fieldiq-muted'}`}
                aria-pressed={handsFree}
                aria-label="Hands-free mode"
              >
                <Headphones size={20} />
              </button>
            </Tooltip>
            <Tooltip content="Voice settings">
              <button
                onClick={() => setShowVoicePicker(!showVoicePicker)}
                className={`touch-target-sm ${showVoicePicker ? 'text-fieldiq-accent' : 'text-fieldiq-muted'}`}
                aria-label="Voice settings"
              >
                <Settings size={20} />
              </button>
            </Tooltip>
            {!isSales && (
            <Tooltip content="Agent Activity">
              <button
                onClick={() => setShowActivityPanel(prev => !prev)}
                className={`touch-target-sm relative ${showActivityPanel ? 'text-fieldiq-accent' : 'text-fieldiq-muted'}`}
                aria-label="Agent Activity"
              >
                <Bell size={20} />
                {agentRuns.length > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
                    {agentRuns.length}
                  </span>
                )}
              </button>
            </Tooltip>
            )}
            <button
              onClick={() => { loadConversations(); setView('history'); }}
              className="touch-target-sm text-fieldiq-muted"
              aria-label="Conversation history"
            >
              <MessageSquare size={20} />
            </button>
            <button onClick={startNewChat} className="touch-target-sm text-fieldiq-muted" aria-label="New chat">
              <Plus size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Voice Settings Panel */}
      {showVoicePicker && (
        <div className="bg-fieldiq-card border-b border-fieldiq-border px-4 py-3 shrink-0">
          <p className="text-xs text-fieldiq-muted uppercase tracking-wider font-semibold mb-2">Conversation</p>
          <div className="space-y-2 mb-4">
            <button
              type="button"
              onClick={toggleHandsFree}
              aria-pressed={handsFree}
              className="w-full flex items-start justify-between gap-3 text-left rounded-lg px-3 py-2 border border-fieldiq-border bg-fieldiq-dark hover:border-fieldiq-accent/50 transition-colors min-h-[44px]"
            >
              <span>
                <span className="text-sm font-medium text-white">Hands-free</span>
                <span className="block text-xs text-fieldiq-muted">
                  The mic closes when you stop talking and reopens when the agent does.
                </span>
              </span>
              <span className={`text-xs font-semibold shrink-0 mt-0.5 ${handsFree ? 'text-fieldiq-accent' : 'text-fieldiq-muted'}`}>
                {handsFree ? 'ON' : 'OFF'}
              </span>
            </button>

            {/*
              * Only offered once hands-free is on, because it has no meaning
              * without it — and kept its own switch because it is the one part
              * of this that can only be proven on a handset. See
              * BARGE_IN_STORAGE_KEY.
              */}
            {handsFree && (
              <button
                type="button"
                onClick={toggleBargeIn}
                aria-pressed={bargeIn}
                className="w-full flex items-start justify-between gap-3 text-left rounded-lg px-3 py-2 border border-fieldiq-border bg-fieldiq-dark hover:border-fieldiq-accent/50 transition-colors min-h-[44px]"
              >
                <span>
                  <span className="text-sm font-medium text-white">Talk over the agent</span>
                  <span className="block text-xs text-fieldiq-muted">
                    Experimental. Keeps the mic open while the agent speaks so you can cut in
                    without tapping. Turn it off if the agent interrupts itself.
                  </span>
                </span>
                <span className={`text-xs font-semibold shrink-0 mt-0.5 ${bargeIn ? 'text-fieldiq-accent' : 'text-fieldiq-muted'}`}>
                  {bargeIn ? 'ON' : 'OFF'}
                </span>
              </button>
            )}
          </div>
          <p className="text-xs text-fieldiq-muted uppercase tracking-wider font-semibold mb-2">Choose AI Voice</p>
          <div className="grid grid-cols-2 gap-2">
            {VOICE_OPTIONS.map((v) => (
              <button
                key={v.id}
                onClick={() => {
                  setSelectedVoice(v.id);
                  localStorage.setItem(VOICE_STORAGE_KEY, v.id);
                  setShowVoicePicker(false);
                }}
                className={`text-left rounded-lg px-3 py-2 border transition-colors ${
                  selectedVoice === v.id
                    ? 'border-fieldiq-accent bg-fieldiq-accent/10'
                    : 'border-fieldiq-border bg-fieldiq-dark hover:border-fieldiq-accent/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-white">{v.name}</span>
                  {selectedVoice === v.id && <Check size={14} className="text-fieldiq-accent" />}
                </div>
                <span className="text-xs text-fieldiq-muted">{v.gender} · {v.desc}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Agent Activity Panel */}
      {showActivityPanel && (
        <div className="bg-fieldiq-card border-b border-fieldiq-border px-4 py-3 shrink-0 max-h-[50vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">Agent Activity</h3>
            <button
              onClick={() => setShowActivityPanel(false)}
              className="touch-target-sm text-fieldiq-muted hover:text-white"
              aria-label="Close agent activity"
            >
              <X size={18} />
            </button>
          </div>

          {agentRunsError != null && (
            /*
             * In place of the empty state when there are no rows; above the rows
             * when an earlier load succeeded, since those rows may be stale.
             * Retry only when unreachable — a refusal will be refused again.
             */
            <div className={agentRuns.length === 0 ? 'py-4' : 'mb-3'}>
              <LoadFailureNotice error={agentRunsError} subject="agent activity" allowed="admins, office staff and supervisors" />
              {classifyLoadFailure(agentRunsError) === 'unreachable' && (
                <button
                  type="button"
                  onClick={() => { fetchAgentRuns(); }}
                  className="mt-2 min-h-[44px] px-4 py-2 text-sm font-medium bg-fieldiq-card border border-fieldiq-border text-fieldiq-text hover:bg-fieldiq-dark/50 rounded-lg transition-colors"
                >
                  Retry
                </button>
              )}
            </div>
          )}

          {stageProposals.length > 0 && (
            <div role="list" aria-label="Scout stage proposals" className="mb-3 space-y-2">
              {stageProposals.map((p) => (
                <div key={p.runId} role="listitem">
                  <StageProposalCard
                    proposal={p}
                    showDeal
                    canDecide={canChangeOpportunity(user as any, { ownerUserId: p.ownerUserId })}
                    onDecided={() => { fetchAgentRuns(); }}
                  />
                </div>
              ))}
            </div>
          )}

          {genericRuns.length === 0 ? (
            agentRunsError == null && stageProposals.length === 0 && (
              <p className="text-center text-fieldiq-muted text-sm py-6">No pending agent activity</p>
            )
          ) : (
            <div role="list" aria-label="Agent activity" className="space-y-2">
              {genericRuns.map((run) => {
                const isExpanded = expandedRunId === run.id;
                return (
                  <div key={run.id} role="listitem" className="border border-fieldiq-border rounded-lg p-4">
                    <button
                      type="button"
                      onClick={() => setExpandedRunId(isExpanded ? null : run.id)}
                      className="w-full text-left min-h-[44px]"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-white">{agentRunLabel(run.agent_name)}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-fieldiq-muted">{timeAgo(run.created_at)}</span>
                          <ChevronDown size={14} className={`text-fieldiq-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      </div>
                      {/* Wraps when open: this line is the run's summary, and the fallback body does not repeat it. */}
                      <p className={`text-xs text-fieldiq-text mt-1 ${isExpanded ? 'break-words' : 'truncate'}`}>{getPayloadPreview(run)}</p>
                    </button>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-fieldiq-border">
                        {renderRunDetail(run)}
                      </div>
                    )}

                    <div className="flex items-center gap-2 mt-3">
                      {run.informational === true ? (
                        /* Nothing to approve on a wrap or cost analysis — /approve 404s on one. */
                        <Tooltip content="Hides this for everyone in your company">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleAgentAction(run.id, 'read'); }}
                            className="min-h-[44px] px-4 py-2 text-sm font-medium bg-fieldiq-card border border-fieldiq-border text-fieldiq-text hover:bg-fieldiq-dark/50 rounded-lg transition-colors"
                          >
                            Mark read
                          </button>
                        </Tooltip>
                      ) : (
                      <>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleAgentAction(run.id, 'approve'); }}
                        className="min-h-[44px] px-4 py-2 text-sm font-medium bg-green-600 text-white hover:bg-green-700 rounded-lg transition-colors"
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleAgentAction(run.id, 'dismiss'); }}
                        className="min-h-[44px] px-4 py-2 text-sm font-medium text-fieldiq-muted hover:text-white rounded-lg transition-colors"
                      >
                        Dismiss
                      </button>
                      </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.length === 0 ? (
          /* Empty state with suggestions */
          <div className="flex flex-col items-center justify-center pt-8">
            <div className="w-16 h-16 rounded-full bg-fieldiq-accent/10 flex items-center justify-center mb-4">
              <Sparkles size={32} className="text-fieldiq-accent" />
            </div>
            <h2 className="text-white font-bold text-lg mb-1">FieldIQ Agent</h2>
            <p className="text-fieldiq-muted text-sm text-center mb-6 px-8">
              Your AI-powered operations assistant. Ask me about jobs, invoices, equipment, schedules, and more.
            </p>
            <div className="w-full space-y-2">
              <p className="text-fieldiq-muted text-xs uppercase tracking-wider font-semibold px-1">Try asking</p>
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(s)}
                  className="w-full text-left bg-fieldiq-card border border-fieldiq-border rounded-xl px-4 py-3 text-sm text-fieldiq-text hover:border-fieldiq-accent/50 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* Message list */
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 rounded-full bg-fieldiq-accent/20 flex items-center justify-center shrink-0 mt-1">
                  <Bot size={14} className="text-fieldiq-accent" />
                </div>
              )}

              {/* A message carrying Scout's card takes the row: the card is a form to read on a phone. */}
              <div className={msg.scoutCard ? 'min-w-0 flex-1 max-w-[560px]' : 'max-w-[85%]'}>
                {/* Voice transcript label for voice-originated user messages */}
                {msg.role === 'user' && msg.fromVoice && msg.transcript && (
                  <div className="flex items-center gap-1 mb-1 justify-end">
                    <Mic size={10} className="text-fieldiq-muted" />
                    <span className="text-[10px] text-fieldiq-muted">Voice message</span>
                  </div>
                )}

                <div
                  className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-fieldiq-accent text-black rounded-br-md'
                      : 'bg-fieldiq-card border border-fieldiq-border text-fieldiq-text rounded-bl-md'
                  }`}
                >
                  {msg.role === 'user' ? (
                    <p>{msg.content}</p>
                  ) : (
                    <div className="space-y-1">{renderContent(msg.content || '')}</div>
                  )}

                  {/* Scout's meeting card. Live only on the newest message, and
                      never while a turn runs — an old draft's Yes would answer a
                      question nobody is asking any more. */}
                  {msg.role === 'assistant' && msg.scoutCard && (
                    <ScoutMeetingCard
                      card={msg.scoutCard}
                      active={!loading && msg.id === messages[messages.length - 1]?.id}
                      onReply={(text) => sendMessage(text)}
                    />
                  )}

                  {/* What this turn wrote, kept after the turn ended. */}
                  {msg.role === 'assistant' && msg.tools && msg.tools.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-fieldiq-border/60">
                      {renderToolChips(msg.tools)}
                    </div>
                  )}

                  <div className={`flex items-center gap-2 mt-1.5 ${msg.role === 'user' ? 'justify-end' : 'justify-between'}`}>
                    <p className={`text-[10px] ${msg.role === 'user' ? 'text-black/50' : 'text-fieldiq-muted'}`}>
                      {formatTime(msg.created_at)}
                    </p>

                    {/* Speaker button on assistant messages */}
                    {msg.role === 'assistant' && msg.content && (
                      <Tooltip content={playingMessageId === msg.id ? 'Stop' : 'Listen'}>
                        <button
                          onClick={() => playTTS(msg.content!, msg.id)}
                          className="text-fieldiq-muted hover:text-fieldiq-accent transition-colors"
                          aria-label={playingMessageId === msg.id ? 'Stop' : 'Listen'}
                        >
                          {ttsLoading === msg.id ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : playingMessageId === msg.id ? (
                            <VolumeX size={12} />
                          ) : (
                            <Volume2 size={12} />
                          )}
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>

              {msg.role === 'user' && (
                <div className="w-7 h-7 rounded-full bg-fieldiq-border flex items-center justify-center shrink-0 mt-1">
                  <User size={14} className="text-fieldiq-text" />
                </div>
              )}
            </div>
          ))
        )}

        {/* Streaming / Loading indicator */}
        {loading && (
          <div className="flex gap-3">
            <div className="w-7 h-7 rounded-full bg-fieldiq-accent/20 flex items-center justify-center shrink-0 mt-1">
              <Bot size={14} className="text-fieldiq-accent" />
            </div>
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl rounded-bl-md px-4 py-3 max-w-[85%]">
              {/* Tool activity, while the turn runs: every call, in progress. */}
              {activeTools.length > 0 && renderToolChips(activeTools)}

              {/* Streaming text */}
              {streamingText ? (
                <div className="text-sm text-fieldiq-text leading-relaxed space-y-1">
                  {renderContent(streamingText)}
                  <span className="inline-block w-1.5 h-4 bg-fieldiq-accent/70 animate-pulse ml-0.5 align-middle" />
                </div>
              ) : !activeTools.length && (
                <div className="flex items-center gap-2 text-sm text-fieldiq-muted">
                  <Wrench size={14} className="animate-spin" />
                  <span>{voiceLoading ? 'Processing voice...' : 'Thinking...'}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input bar */}
      {/* BottomNav renders outside <Routes> (App.tsx) and is md:hidden, so it overlays
          this bar on mobile — the mic was landing under the nav and the home indicator.
          Clear the full bottom chrome below md, plus 12px so the mic does not sit flush
          on the nav; keep the plain 12px gutter at md+. */}
      <div className="shrink-0 bg-fieldiq-dark border-t border-fieldiq-border px-4 pt-3 pb-[calc(var(--bottom-chrome)+12px)] md:pb-3">
        {micInvite && !listeningMode && !isRecording && !loading && (
          <button
            type="button"
            onClick={() => { setMicInvite(false); handleMicClick(); }}
            data-testid="scout-mic-invite"
            className="mb-3 w-full min-h-[56px] rounded-xl bg-fieldiq-accent text-black flex items-center justify-center gap-2 text-base font-bold active:opacity-90"
          >
            <Mic size={20} aria-hidden="true" />
            Tap to talk to Scout
          </button>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isRecording ? 'Listening...' : listeningMode ? 'Speak or type...' : 'Ask me anything...'}
            rows={1}
            className="flex-1 bg-fieldiq-card border border-fieldiq-border rounded-xl px-4 py-3 text-sm text-white placeholder-fieldiq-muted resize-none focus:outline-none focus:border-fieldiq-accent/50"
            disabled={loading || isRecording}
          />

          {/* Mic button */}
          <Tooltip content={listeningMode ? 'Listening — tap to stop' : 'Start voice mode'} className="shrink-0">
            <button
              onClick={handleMicClick}
              disabled={loading && !listeningMode}
              className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                isRecording
                  ? 'bg-red-500 text-white'
                  : listeningMode
                    ? 'bg-fieldiq-accent/20 border border-fieldiq-accent text-fieldiq-accent'
                    : voiceLoading
                      ? 'bg-fieldiq-card border border-fieldiq-border text-fieldiq-muted'
                      : 'bg-fieldiq-card border border-fieldiq-border text-fieldiq-muted hover:text-red-400 hover:border-red-400/50'
              }`}
              aria-label={listeningMode ? 'Listening — tap to stop' : 'Start voice mode'}
            >
              {voiceLoading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : isRecording ? (
                <div className="relative">
                  <span className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-50" />
                  <Square size={14} className="relative" />
                </div>
              ) : listeningMode ? (
                <div className="relative">
                  <span className="absolute inset-0 rounded-full bg-fieldiq-accent animate-ping opacity-30" />
                  <Mic size={18} className="relative" />
                </div>
              ) : (
                <Mic size={18} />
              )}
            </button>
          </Tooltip>

          {/* Send button */}
          <button
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
              input.trim() && !loading
                ? 'bg-fieldiq-accent text-black'
                : 'bg-fieldiq-card border border-fieldiq-border text-fieldiq-muted'
            }`}
          >
            {loading && !voiceLoading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>

        {/*
          * THE STATE OF THE VOICE SESSION, SAID OUT LOUD, AT EVERY MOMENT.
          *
          * `data-voice-state` carries the same four values the label does, so
          * what a test reads and what the user reads cannot drift apart.
          *
          * The End button appears in all three arms and always calls the same
          * `endSession`. It is reachable mid-recording, mid-thinking and
          * mid-read-back, and it stops the recorder, the barge-in listen and
          * the speech queue in every one of them.
          */}
        {(listeningMode || isRecording) && (
          <div className="mt-2 space-y-2" data-testid="voice-session" data-voice-state={voiceState}>
            {isRecording ? (
              <div className="flex items-center gap-2">
                {/*
                  * Hands-free sends on its own, so the big control says what it
                  * is doing and stays tappable as a way to send NOW. With
                  * hands-free off this is the tap-to-talk bar exactly as it has
                  * always been: same words, same colours, same handler.
                  */}
                <button
                  onClick={() => stopAndSend({ byUser: true })}
                  className="flex-1 py-3 rounded-xl bg-red-500/20 border border-red-500/40 flex items-center justify-center gap-3 active:bg-red-500/30 transition-colors"
                >
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
                  </span>
                  <span className="text-sm font-semibold text-red-400">
                    {handsFree
                      ? `${VOICE_STATE_LABEL[voiceState]} · ${formatRecordingTime(recordingElapsed)}`
                      : `Tap when done · ${formatRecordingTime(recordingElapsed)}`}
                  </span>
                  {handsFree && <LevelMeter level={micLevel} />}
                </button>
                <button
                  onClick={endSession}
                  aria-label="End voice session"
                  className="py-3 px-4 rounded-xl bg-fieldiq-card border border-fieldiq-border flex items-center justify-center gap-1.5 active:bg-fieldiq-border/50 transition-colors"
                >
                  <PhoneOff size={16} className="text-fieldiq-muted" />
                  <span className="text-sm font-medium text-fieldiq-muted">End</span>
                </button>
              </div>
            ) : showTapHint ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleMicClick}
                  className="flex-1 py-3 rounded-xl bg-fieldiq-accent/10 border border-fieldiq-accent/30 flex items-center justify-center gap-2 active:bg-fieldiq-accent/20 transition-colors"
                >
                  <Mic size={16} className="text-fieldiq-accent animate-pulse" />
                  <span className="text-sm font-medium text-fieldiq-accent">Tap to respond</span>
                </button>
                <button
                  onClick={endSession}
                  aria-label="End voice session"
                  className="py-3 px-4 rounded-xl bg-red-500/15 border border-red-500/30 flex items-center justify-center gap-2 active:bg-red-500/25 transition-colors"
                >
                  <PhoneOff size={16} className="text-red-400" />
                  <span className="text-sm font-medium text-red-400">End</span>
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-1">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-fieldiq-accent opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-fieldiq-accent" />
                </span>
                <span className="flex-1 text-xs font-medium text-fieldiq-accent">
                  {VOICE_STATE_LABEL[voiceState]}
                </span>
                <button
                  onClick={endSession}
                  aria-label="End voice session"
                  className="py-1.5 px-3 rounded-lg bg-red-500/15 border border-red-500/30 flex items-center gap-1.5 active:bg-red-500/25 transition-colors"
                >
                  <PhoneOff size={14} className="text-red-400" />
                  <span className="text-xs font-medium text-red-400">End</span>
                </button>
              </div>
            )}

            {/*
              * WHY A DISCARDED CLIP HAS TO SAY SO.
              *
              * Dropping near-silence is the right call and it happens without
              * the user asking. Unannounced it is indistinguishable from the
              * agent ignoring them — they spoke, or thought they did, and
              * nothing came back. `role="status"` so it is announced rather
              * than only drawn.
              */}
            {dropNotice && (
              <p role="status" className="text-xs text-fieldiq-muted px-1">
                {dropNotice}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
